import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiGrid,
  FiHash,
  FiLayers,
  FiRotateCcw,
  FiX,
} from "react-icons/fi";
import "./OccupancyGrid.css";
import { usePmsTheme } from "./PmsThemeContext";
import { createReservationTranslator, getReservationLocale } from "../reservations/reservationI18n";
import { OCCUPANCY_GRID_SPANISH } from "./occupancyGridMessages";
import { getBookerDisplayName, isCompanyBooker } from "../reservations/bookerPresentation";
import { isNonOccupyingReservation } from "../reservations/reservationOccupancy";
import { getDistinctRoomProposals } from "../reservations/unassignedRoomProposals";

function normalizeISODate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function isInHouseReservation(reservation) {
  if (!reservation || reservation?.__type === "room_block") return false;
  const normalizedStatus = String(reservation?.status || reservation?.reservation_status || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  return normalizedStatus === "checked in" || normalizedStatus === "in house";
}

function toISODateString(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseISO(isoDate) {
  const normalized = normalizeISODate(isoDate);
  if (!normalized) return null;
  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function addDays(dateObj, days) {
  const next = new Date(dateObj);
  next.setDate(next.getDate() + days);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDaysISO(isoDate, days) {
  const parsed = parseISO(isoDate);
  if (!parsed) return "";
  return toISODateString(addDays(parsed, days));
}

function dayDiff(startIso, endIso) {
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  if (!start || !end) return 0;
  return Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && aEnd > bStart;
}

function sanitizeRange(startIso, endIso, fallbackDays, referenceDate = "") {
  const safeFallback = Math.max(1, Number(fallbackDays) || 1);
  const todayIso = parseISO(referenceDate) ? normalizeISODate(referenceDate) : toISODateString(new Date());

  let start = normalizeISODate(startIso);
  if (!parseISO(start)) start = todayIso;

  let end = normalizeISODate(endIso);
  if (!parseISO(end)) end = addDaysISO(start, safeFallback - 1);

  if (end < start) end = start;

  return { start, end };
}

function buildDateRange(startIso, endIso) {
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  if (!start || !end || end < start) return [];

  const totalDays = dayDiff(startIso, endIso) + 1;
  const dates = new Array(totalDays);
  for (let i = 0; i < totalDays; i += 1) {
    dates[i] = toISODateString(addDays(start, i));
  }
  return dates;
}

function toDayLabel(isoDate, locale = "en-GB") {
  const parsed = parseISO(isoDate);
  if (!parsed) return isoDate;
  return parsed.toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function toWeekdayLabel(isoDate, locale = "en-GB") {
  const parsed = parseISO(isoDate);
  if (!parsed) return isoDate;
  return parsed.toLocaleDateString(locale, { weekday: "short" });
}

function toCalendarDateLabel(isoDate, locale = "en-GB") {
  const parsed = parseISO(isoDate);
  if (!parsed) return isoDate;
  return parsed.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function getGuestDisplayName(guest) {
  const customer = guest?.customer || {};
  const full = `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim();
  if (full) return full;
  return String(customer?.name || customer?.full_name || "").trim();
}

function getWaitlistEntryForBlock(reservation) {
  if (reservation?.__type !== "room_block") return null;
  const entries = Array.isArray(reservation?.waitlist_entries) ? reservation.waitlist_entries : [];
  return entries[0] || null;
}

function getReservationLabel(reservation, options = {}) {
  const t = options.t;
  if (reservation?.__type === "room_block") {
    const waitlistEntry = getWaitlistEntryForBlock(reservation);
    const waitlistGuestName = String(waitlistEntry?.guest_name || "").trim();
    if (waitlistEntry) {
      return waitlistGuestName ? t("Waitlist · {name}", { name: waitlistGuestName }) : t("Waitlist");
    }
    return t("Room Block");
  }

  const groupBookerByGroupId = options?.groupBookerByGroupId || {};

  const guests = Array.isArray(reservation?.guests) ? reservation.guests : [];
  const mainGuest = guests.find((g) => g?.is_main_guest);
  const mainGuestName = getGuestDisplayName(mainGuest);
  if (mainGuestName) return mainGuestName;
  if (isCompanyBooker(reservation)) {
    const companyName = getBookerDisplayName(reservation);
    if (companyName) return companyName;
  }

  const directBooker = guests.find((g) => g?.is_booker);
  const directBookerName = getGuestDisplayName(directBooker);
  if (directBookerName) return directBookerName;

  const reservationGroupId =
    reservation?.reservation_group_id ??
    reservation?.reservation_group?.id ??
    reservation?.group_id ??
    null;
  if (reservationGroupId != null) {
    const groupBookerName = String(groupBookerByGroupId?.[String(reservationGroupId)] || "").trim();
    if (groupBookerName) return groupBookerName;
  }

  const groupReservations = Array.isArray(reservation?.group_reservations)
    ? reservation.group_reservations
    : Array.isArray(reservation?.group?.reservations)
      ? reservation.group.reservations
      : [];
  for (const groupReservation of groupReservations) {
    const groupGuests = Array.isArray(groupReservation?.guests) ? groupReservation.guests : [];
    const groupBooker = groupGuests.find((g) => g?.is_booker);
    const groupBookerName = getGuestDisplayName(groupBooker);
    if (groupBookerName) return groupBookerName;
  }

  const fallbackGuest = guests[0];
  const fallbackGuestName = getGuestDisplayName(fallbackGuest);
  if (fallbackGuestName) return fallbackGuestName;

  const directCustomerName =
    reservation?.customer_name ||
    reservation?.booker_name ||
    reservation?.main_guest_name ||
    reservation?.guest_name ||
    reservation?.customer?.full_name ||
    reservation?.customer?.name ||
    "";
  if (String(directCustomerName || "").trim()) return String(directCustomerName).trim();

  const locatorFallback = reservation?.group_locator || reservation?.locator || "";
  if (String(locatorFallback || "").trim()) return String(locatorFallback).trim();

  return t("Reservation");
}

function segmentForVisibleWindow(reservation, visibleDates) {
  const inDate = parseISO(reservation?.check_in_date);
  const outDate = parseISO(reservation?.check_out_date);
  if (!inDate || !outDate || !Array.isArray(visibleDates) || visibleDates.length === 0) return null;

  const windowStart = parseISO(visibleDates[0]);
  const windowEndExclusive = addDays(parseISO(visibleDates[visibleDates.length - 1]), 1);
  if (!windowStart || !windowEndExclusive) return null;

  if (!overlaps(inDate, outDate, windowStart, windowEndExclusive)) return null;

  let startIdx = 0;
  while (startIdx < visibleDates.length) {
    const day = parseISO(visibleDates[startIdx]);
    if (day && day >= inDate && day < outDate) break;
    startIdx += 1;
  }

  let endIdx = startIdx;
  while (endIdx < visibleDates.length) {
    const day = parseISO(visibleDates[endIdx]);
    if (!day || !(day >= inDate && day < outDate)) break;
    endIdx += 1;
  }

  const span = endIdx - startIdx;
  if (span <= 0) return null;

  return { startIdx, span };
}

function normalizeAssignmentStayDate(row) {
  return normalizeISODate(row?.stay_date ?? row?.date ?? row?.stayDate);
}

function getAssignmentRoomPayload(row) {
  const roomId = row?.room_id ?? row?.room?.id ?? null;
  const roomNumber = row?.room?.room_number ?? row?.room_number ?? null;
  const roomCategoryId = row?.room?.room_category_id ?? row?.room_category_id ?? null;
  if (roomId == null && roomNumber == null && roomCategoryId == null) return null;
  return {
    ...(row?.room && typeof row.room === "object" ? row.room : {}),
    ...(roomId != null ? { id: roomId } : {}),
    ...(roomNumber != null ? { room_number: roomNumber } : {}),
    ...(roomCategoryId != null ? { room_category_id: roomCategoryId } : {}),
  };
}

function buildReservationRoomAssignmentSegments(reservation) {
  if (!reservation || reservation?.__type === "room_block") return [reservation].filter(Boolean);

  const rows = (Array.isArray(reservation?.room_assignments) ? reservation.room_assignments : [])
    .map((row) => ({
      ...row,
      stayDate: normalizeAssignmentStayDate(row),
      roomId: row?.room_id ?? row?.room?.id ?? null,
      room: getAssignmentRoomPayload(row),
    }))
    .filter((row) => row.stayDate && row.roomId != null)
    .sort((a, b) => a.stayDate.localeCompare(b.stayDate));

  if (rows.length === 0) return [reservation];

  const groups = [];
  rows.forEach((row) => {
    const previousGroup = groups[groups.length - 1];
    const previousRow = previousGroup?.rows?.[previousGroup.rows.length - 1];
    const continuesPrevious =
      previousGroup &&
      String(previousGroup.roomId) === String(row.roomId) &&
      previousRow?.stayDate &&
      addDaysISO(previousRow.stayDate, 1) === row.stayDate;

    if (continuesPrevious) {
      previousGroup.rows.push(row);
      return;
    }

    groups.push({
      roomId: row.roomId,
      room: row.room,
      rows: [row],
    });
  });

  if (groups.length <= 1) return [reservation];

  return groups.map((group, index) => {
    const startDate = group.rows[0]?.stayDate;
    const lastDate = group.rows[group.rows.length - 1]?.stayDate;
    const endDate = addDaysISO(lastDate, 1);
    const previousGroup = groups[index - 1] || null;
    const nextGroup = groups[index + 1] || null;

    return {
      ...reservation,
      check_in_date: startDate || reservation.check_in_date,
      check_out_date: endDate || reservation.check_out_date,
      room_id: group.roomId,
      effective_room_id: group.roomId,
      room: group.room || reservation.room,
      effective_room: group.room || reservation.effective_room || reservation.room,
      effective_room_number:
        group.room?.room_number ?? reservation.effective_room_number ?? reservation.room_number ?? null,
      __room_assignment_segment: {
        index,
        total: groups.length,
        date_from: startDate || null,
        date_to: endDate || null,
        moved_from_room_number: previousGroup?.room?.room_number ?? null,
        moved_to_room_number: nextGroup?.room?.room_number ?? null,
      },
    };
  });
}

function expandReservationsByRoomAssignmentSegments(reservations) {
  return (Array.isArray(reservations) ? reservations : []).flatMap((reservation) =>
    buildReservationRoomAssignmentSegments(reservation)
  );
}

function buildFixedLanesForCategory(reservationsForCategory, configuredLaneCount) {
  const sorted = [...reservationsForCategory].sort((a, b) => {
    const aIn = parseISO(a?.check_in_date);
    const bIn = parseISO(b?.check_in_date);
    return (aIn?.getTime() || 0) - (bIn?.getTime() || 0);
  });

  const baseLaneCount = Math.max(1, Number(configuredLaneCount || 1));
  const lanes = Array.from({ length: baseLaneCount }, () => []);

  const laneHasOverlap = (lane, reservation) => {
    const inDate = parseISO(reservation?.check_in_date);
    const outDate = parseISO(reservation?.check_out_date);
    if (!inDate || !outDate) return false;

    for (const laneItem of lane) {
      const existingIn = parseISO(laneItem?.reservation?.check_in_date);
      const existingOut = parseISO(laneItem?.reservation?.check_out_date);
      if (!existingIn || !existingOut) continue;
      if (overlaps(inDate, outDate, existingIn, existingOut)) return true;
    }
    return false;
  };

  for (const reservation of sorted) {
    let placed = false;

    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex += 1) {
      if (!laneHasOverlap(lanes[laneIndex], reservation)) {
        lanes[laneIndex].push({
          reservation,
          conflict: laneIndex >= baseLaneCount,
        });
        placed = true;
        break;
      }
    }

    if (!placed) {
      lanes.push([{ reservation, conflict: true }]);
    }
  }

  return lanes;
}

function buildOverbookedDateSetForCategory(reservationsForCategory, visibleDates, roomCount) {
  const overbookedDates = new Set();
  const maxRooms = Number(roomCount || 0);
  if (!Array.isArray(visibleDates) || visibleDates.length === 0 || maxRooms <= 0) return overbookedDates;

  const occupancyByDayIndex = new Array(visibleDates.length).fill(0);

  for (const reservation of reservationsForCategory) {
    const segment = segmentForVisibleWindow(reservation, visibleDates);
    if (!segment) continue;

    for (let idx = segment.startIdx; idx < segment.startIdx + segment.span; idx += 1) {
      occupancyByDayIndex[idx] += 1;
    }
  }

  occupancyByDayIndex.forEach((count, idx) => {
    if (count > maxRooms) overbookedDates.add(visibleDates[idx]);
  });

  return overbookedDates;
}

function reservationOccupiesDate(reservation, isoDate) {
  const inDate = parseISO(reservation?.check_in_date);
  const outDate = parseISO(reservation?.check_out_date);
  const day = parseISO(isoDate);
  if (!inDate || !outDate || !day) return false;
  return day >= inDate && day < outDate;
}

function reservationsOverlapByRange(a, b) {
  const aIn = parseISO(a?.check_in_date);
  const aOut = parseISO(a?.check_out_date);
  const bIn = parseISO(b?.check_in_date);
  const bOut = parseISO(b?.check_out_date);
  if (!aIn || !aOut || !bIn || !bOut) return false;
  return overlaps(aIn, aOut, bIn, bOut);
}

function findRoomConflicts({
  reservations,
  candidateReservation,
  targetCategoryId,
  targetRoomId,
  ignoreReservationIds = [],
}) {
  if (!candidateReservation || !targetCategoryId || !targetRoomId) return [];
  const ignored = new Set((Array.isArray(ignoreReservationIds) ? ignoreReservationIds : []).map((id) => String(id)));

  return (Array.isArray(reservations) ? reservations : []).filter((reservation) => {
    if (!reservation || reservation?.id == null || reservation?.__type === "room_block") return false;
    const reservationId = String(reservation.id);
    if (ignored.has(reservationId)) return false;
    const categoryId = getReservationCategoryId(reservation);
    if (!categoryId || categoryId !== String(targetCategoryId)) return false;
    const roomId = getReservationRoomId(reservation);
    if (!roomId || roomId !== String(targetRoomId)) return false;
    return reservationsOverlapByRange(candidateReservation, reservation);
  });
}

function buildOverflowTooltip(dateIso, overflowLabels, t) {
  if (!Array.isArray(overflowLabels) || overflowLabels.length === 0) return "";

  const maxLabels = 6;
  const visibleLabels = overflowLabels.slice(0, maxLabels);
  const remaining = Math.max(0, overflowLabels.length - maxLabels);
  const suffix = remaining > 0 ? t(", +{count} more", { count: remaining }) : "";

  return t(
    overflowLabels.length === 1
      ? "Overbooking {date}: {count} reservation without room. {names}{suffix}"
      : "Overbooking {date}: {count} reservations without room. {names}{suffix}",
    { date: dateIso, count: overflowLabels.length, names: visibleLabels.join(", "), suffix }
  );
}

function buildSegmentOverbookCellStyle(
  segmentStartIdx,
  segmentSpan,
  visibleDates,
  overbookedDates,
  showDaySeparatorsInsideBar,
  todayIso
) {
  if (!segmentSpan || segmentSpan <= 0 || !Array.isArray(visibleDates) || visibleDates.length === 0) {
    return undefined;
  }

  const gradients = [];
  if (showDaySeparatorsInsideBar) {
    const daySeparatorGradient =
      "repeating-linear-gradient(90deg, transparent 0, transparent calc(var(--res-grid-day-width) - 1px), rgba(137, 161, 220, 0.22) calc(var(--res-grid-day-width) - 1px), rgba(137, 161, 220, 0.22) var(--res-grid-day-width))";
    gradients.push(daySeparatorGradient);
  }

  for (let offset = 0; offset < segmentSpan; offset += 1) {
    const dateIso = visibleDates[segmentStartIdx + offset];
    if (!dateIso || !overbookedDates.has(dateIso)) continue;

    const startPct = (offset * 100) / segmentSpan;
    const endPct = ((offset + 1) * 100) / segmentSpan;
    gradients.push(
      `linear-gradient(90deg, transparent ${startPct}%, rgba(171, 47, 60, 0.22) ${startPct}%, rgba(171, 47, 60, 0.22) ${endPct}%, transparent ${endPct}%)`
    );
  }

  const todayOffset = visibleDates
    .slice(segmentStartIdx, segmentStartIdx + segmentSpan)
    .findIndex((dateIso) => dateIso === todayIso);
  if (todayOffset >= 0) {
    const startPct = (todayOffset * 100) / segmentSpan;
    const endPct = ((todayOffset + 1) * 100) / segmentSpan;
    gradients.push(
      `linear-gradient(90deg, transparent ${startPct}%, rgba(61, 182, 233, 0.085) ${startPct}%, rgba(61, 182, 233, 0.085) ${endPct}%, transparent ${endPct}%)`
    );
  }

  if (gradients.length === 0) return undefined;
  return { backgroundImage: gradients.join(", ") };
}

function normalizeRoomReference(reservation) {
  const roomId =
    reservation?.effective_room_id ??
    reservation?.room_id ??
    reservation?.effective_room?.id ??
    reservation?.room?.id ??
    null;
  const rawRoomNumber =
    reservation?.effective_room_number ??
    reservation?.effective_room?.room_number ??
    reservation?.room?.room_number ??
    reservation?.room_number ??
    null;
  const roomNumber = rawRoomNumber != null ? String(rawRoomNumber).trim() : "";
  return {
    roomId: roomId != null ? String(roomId) : "",
    roomNumber,
    roomNumberKey: roomNumber ? roomNumber.toLowerCase() : "",
  };
}

function getReservationCategoryId(reservation) {
  const categoryId =
    reservation?.room_category_id ??
    reservation?.room_category?.id ??
    reservation?.room?.room_category_id ??
    null;
  return categoryId != null ? String(categoryId) : "";
}

function getReservationRoomId(reservation) {
  const roomId =
    reservation?.effective_room_id ??
    reservation?.room_id ??
    reservation?.effective_room?.id ??
    reservation?.room?.id ??
    null;
  return roomId != null ? String(roomId) : "";
}

function buildUnassignedTooltip(dateIso, labels, t) {
  if (!Array.isArray(labels) || labels.length === 0) return "";

  const maxLabels = 6;
  const visibleLabels = labels.slice(0, maxLabels);
  const remaining = Math.max(0, labels.length - maxLabels);
  const suffix = remaining > 0 ? t(", +{count} more", { count: remaining }) : "";

  return t(
    labels.length === 1
      ? "Unassigned room number {date}: {count} reservation. {names}{suffix}"
      : "Unassigned room number {date}: {count} reservations. {names}{suffix}",
    { date: dateIso, count: labels.length, names: visibleLabels.join(", "), suffix }
  );
}

function buildCategoryRoomDefinitions(categoryId, configuredRoomCount, roomNumbersByCategory) {
  const byCategory = roomNumbersByCategory?.[categoryId] ?? roomNumbersByCategory?.[Number(categoryId)] ?? [];
  const rows = [];
  const seen = new Set();

  if (Array.isArray(byCategory)) {
    for (const room of byCategory) {
      const roomIdRaw = room?.room_id ?? room?.id ?? room?.roomId ?? null;
      const roomId = roomIdRaw != null ? String(roomIdRaw) : "";
      const roomNumberRaw = room?.room_number ?? room?.roomNumber ?? room?.name ?? null;
      const roomNumber = roomNumberRaw != null ? String(roomNumberRaw).trim() : "";
      if (!roomId && !roomNumber) continue;

      const dedupeKey = roomId ? `id:${roomId}` : `num:${roomNumber.toLowerCase()}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      rows.push({
        roomId,
        roomNumber: roomNumber || `Room ${rows.length + 1}`,
        fallbackNumber: roomNumber ? null : rows.length + 1,
      });
    }
  }

  rows.sort((a, b) =>
    String(a?.roomNumber || "").localeCompare(String(b?.roomNumber || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );

  if (rows.length === 0) {
    const fallbackCount = Math.max(1, Number(configuredRoomCount || 0));
    for (let idx = 0; idx < fallbackCount; idx += 1) {
      rows.push({
        roomId: "",
        roomNumber: `Room ${idx + 1}`,
        fallbackNumber: idx + 1,
      });
    }
  }

  return rows;
}

export default function OccupancyGrid({
  reservations = [],
  roomBlocks = [],
  isLoading = false,
  businessUnitId,
  roomCountsByCategory = {},
  roomCategoryNamesById = {},
  roomNumbersByCategory = {},
  groupBookerNamesByGroupId = {},
  onFetchAvailableRooms,
  onAssignRoom,
  daysPerPage = 9,
  initialGridViewMode = "lanes",
  initialStartDate = "",
  highlightReservationId = "",
  onOpenReservation,
  onBackToList,
  readOnly = false,
  referenceDate = "",
}) {
  const { languageId } = usePmsTheme();
  const t = useMemo(() => createReservationTranslator(languageId, OCCUPANCY_GRID_SPANISH), [languageId]);
  const locale = getReservationLocale(languageId);
  const translateError = (message) => t(
    languageId === "es" && !Object.prototype.hasOwnProperty.call(OCCUPANCY_GRID_SPANISH, message)
      ? "Operation failed."
      : message
  );
  const initialVisibleDays = Math.max(1, Number(daysPerPage) || 1);
  const currentDateIso = parseISO(referenceDate) ? normalizeISODate(referenceDate) : toISODateString(new Date());
  const canAssignRooms = !readOnly && typeof onAssignRoom === "function";
  const canAssignRoomsRef = useRef(canAssignRooms);
  canAssignRoomsRef.current = canAssignRooms;
  const getDefaultRange = useCallback(() => {
    const startIso = normalizeISODate(initialStartDate) || currentDateIso;
    return sanitizeRange(startIso, addDaysISO(startIso, initialVisibleDays - 1), initialVisibleDays, currentDateIso);
  }, [currentDateIso, initialStartDate, initialVisibleDays]);

  const [range, setRange] = useState(() => getDefaultRange());
  const [viewMode, setViewMode] = useState(initialGridViewMode === "room_numbers" ? "room_numbers" : "lanes");
  const [unassignedPanel, setUnassignedPanel] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState("");
  const [gridActionError, setGridActionError] = useState("");
  const [pendingMutationCount, setPendingMutationCount] = useState(0);
  const [availableRoomsByReservation, setAvailableRoomsByReservation] = useState({});
  const [selectedRoomByReservation, setSelectedRoomByReservation] = useState({});
  const [priorityRoomReservationId, setPriorityRoomReservationId] = useState("");
  const [optimisticAssignmentsByReservationId, setOptimisticAssignments] = useState({});
  const optimisticAssignmentsRef = useRef({});
  const setOptimisticAssignmentsByReservationId = useCallback((update) => {
    // Keep assignments visible to rapid actions before React commits the next render.
    const next = typeof update === "function" ? update(optimisticAssignmentsRef.current) : update;
    optimisticAssignmentsRef.current = next;
    setOptimisticAssignments(next);
  }, []);
  const [draggedReservationId, setDraggedReservationId] = useState("");
  const [dragOverTargetKey, setDragOverTargetKey] = useState("");
  const [roomMoveOverlay, setRoomMoveOverlay] = useState({ width: 0, height: 0, connectors: [] });
  const tableWrapRef = useRef(null);
  const tablePlaneRef = useRef(null);
  const panelLoadSeqRef = useRef(0);
  const dragPayloadRef = useRef(null);
  const mutationQueueRef = useRef([]);
  const mutationQueueRunningRef = useRef(false);
  const isDndDebugEnabled = true;
  const logDnd = (...args) => {
    if (!isDndDebugEnabled) return;
    console.log("[OCC-GRID][DND]", ...args);
  };
  const warnDnd = (...args) => {
    if (!isDndDebugEnabled) return;
    console.warn("[OCC-GRID][DND]", ...args);
  };

  const usingRoomNumbers = viewMode === "room_numbers";
  const isGridBootLoading = Boolean(isLoading);

  useEffect(() => {
    if (!readOnly) return;
    // Discard queued writes and stale availability when entering consultation mode.
    panelLoadSeqRef.current += 1;
    mutationQueueRef.current.splice(0).forEach((entry) => entry.resolve(false));
    setPendingMutationCount(0);
    setOptimisticAssignmentsByReservationId({});
    setAvailableRoomsByReservation({});
    setSelectedRoomByReservation({});
    setPriorityRoomReservationId("");
    setPanelLoading(false);
    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
  }, [readOnly, setOptimisticAssignmentsByReservationId]);

  useEffect(() => {
    if (initialGridViewMode !== "room_numbers") return;
    setViewMode("room_numbers");
  }, [initialGridViewMode]);

  useEffect(() => {
    if (usingRoomNumbers) return;
    panelLoadSeqRef.current += 1;
    setUnassignedPanel(null);
    setPanelLoading(false);
    setPanelError("");
    setGridActionError("");
    setPendingMutationCount(0);
    setAvailableRoomsByReservation({});
    setSelectedRoomByReservation({});
    setPriorityRoomReservationId("");
    setOptimisticAssignmentsByReservationId({});
    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
    mutationQueueRef.current = [];
    mutationQueueRunningRef.current = false;
  }, [usingRoomNumbers, setOptimisticAssignmentsByReservationId]);

  useEffect(() => {
    const normalized = getDefaultRange();
    setRange((prev) =>
      prev.start === normalized.start && prev.end === normalized.end ? prev : normalized
    );
  }, [businessUnitId, getDefaultRange]);



  const visibleDates = useMemo(() => buildDateRange(range.start, range.end), [range.end, range.start]);
  const showDaySeparatorsInsideBar = visibleDates.length >= 15;

  const effectiveReservations = useMemo(() => {
    const reservationsList = (Array.isArray(reservations) ? reservations : [])
      .filter((reservation) => !isNonOccupyingReservation(reservation));
    const optimisticKeys = Object.keys(optimisticAssignmentsByReservationId || {});
    if (optimisticKeys.length === 0) return reservationsList;

    return reservationsList.map((reservation) => {
      const reservationId = reservation?.id != null ? String(reservation.id) : "";
      const optimisticAssignment = optimisticAssignmentsByReservationId?.[reservationId];
      if (!optimisticAssignment) return reservation;

      if (optimisticAssignment.room_id == null) {
        return {
          ...reservation,
          room_id: null,
          effective_room_id: null,
          effective_room_number: null,
          room: null,
          effective_room: null,
        };
      }

      return {
        ...reservation,
        room_id: optimisticAssignment.room_id,
        effective_room_id: optimisticAssignment.effective_room_id ?? optimisticAssignment.room_id,
        effective_room_number:
          optimisticAssignment.effective_room_number ??
          optimisticAssignment.room?.room_number ??
          reservation?.effective_room_number ??
          reservation?.room?.room_number ??
          null,
        room: {
          ...(reservation?.room || {}),
          ...(optimisticAssignment?.room || {}),
          id: optimisticAssignment.room_id,
        },
        effective_room: {
          ...(reservation?.effective_room || reservation?.room || {}),
          ...(optimisticAssignment?.effective_room || optimisticAssignment?.room || {}),
          id: optimisticAssignment.effective_room_id ?? optimisticAssignment.room_id,
        },
      };
    });
  }, [optimisticAssignmentsByReservationId, reservations]);

  const reservationById = useMemo(() => {
    const map = new Map();
    (Array.isArray(effectiveReservations) ? effectiveReservations : []).forEach((reservation) => {
      if (!reservation || reservation?.__type === "room_block" || reservation?.id == null) return;
      map.set(String(reservation.id), reservation);
    });
    return map;
  }, [effectiveReservations]);

  const groupBookerByGroupId = useMemo(() => {
    const map = {};
    Object.entries(groupBookerNamesByGroupId || {}).forEach(([groupId, name]) => {
      const cleanName = String(name || "").trim();
      if (!cleanName) return;
      map[String(groupId)] = cleanName;
    });
    const reservationsList = Array.isArray(effectiveReservations) ? effectiveReservations : [];
    reservationsList.forEach((reservation) => {
      if (!reservation || reservation?.__type === "room_block") return;

      const reservationGroupId =
        reservation?.reservation_group_id ??
        reservation?.reservation_group?.id ??
        reservation?.group_id ??
        null;
      if (reservationGroupId == null) return;
      const groupKey = String(reservationGroupId);

      const guests = Array.isArray(reservation?.guests) ? reservation.guests : [];
      const ownBooker = guests.find((guest) => guest?.is_booker);
      const ownBookerName = getBookerDisplayName(reservation) || getGuestDisplayName(ownBooker);
      if (ownBookerName && !map[groupKey]) {
        map[groupKey] = ownBookerName;
      }

      const groupReservations = Array.isArray(reservation?.group_reservations)
        ? reservation.group_reservations
        : Array.isArray(reservation?.group?.reservations)
          ? reservation.group.reservations
          : [];

      groupReservations.forEach((groupReservation) => {
        const nestedGroupId =
          groupReservation?.reservation_group_id ??
          groupReservation?.reservation_group?.id ??
          groupReservation?.group_id ??
          reservationGroupId;
        if (nestedGroupId == null) return;
        const nestedKey = String(nestedGroupId);
        const nestedGuests = Array.isArray(groupReservation?.guests) ? groupReservation.guests : [];
        const nestedBooker = nestedGuests.find((guest) => guest?.is_booker);
        const nestedBookerName = getBookerDisplayName(groupReservation) || getGuestDisplayName(nestedBooker);
        if (nestedBookerName && !map[nestedKey]) {
          map[nestedKey] = nestedBookerName;
        }
      });
    });
    return map;
  }, [effectiveReservations, groupBookerNamesByGroupId]);

  const getReservationLabelForGrid = useCallback(
    (reservation) => getReservationLabel(reservation, { groupBookerByGroupId, t }),
    [groupBookerByGroupId, t]
  );

  const roomNumberById = useMemo(() => {
    const map = {};
    Object.values(roomNumbersByCategory || {}).forEach((rows) => {
      if (!Array.isArray(rows)) return;
      rows.forEach((room) => {
        const roomId = room?.room_id ?? room?.id ?? null;
        if (roomId == null) return;
        const number = room?.room_number ?? room?.roomNumber ?? room?.name ?? "";
        map[String(roomId)] = String(number || `Room ${roomId}`);
      });
    });

    (Array.isArray(effectiveReservations) ? effectiveReservations : []).forEach((reservation) => {
      const roomId =
        reservation?.effective_room_id ??
        reservation?.room_id ??
        reservation?.effective_room?.id ??
        reservation?.room?.id ??
        null;
      const roomNumber =
        reservation?.effective_room_number ??
        reservation?.effective_room?.room_number ??
        reservation?.room?.room_number ??
        reservation?.room_number ??
        null;
      if (roomId == null || roomNumber == null) return;
      if (!map[String(roomId)]) map[String(roomId)] = String(roomNumber);
    });

    return map;
  }, [effectiveReservations, roomNumbersByCategory]);

  const roomLabelById = useMemo(() => {
    const labels = { ...roomNumberById };
    Object.values(roomNumbersByCategory || {}).forEach((rooms) => {
      if (!Array.isArray(rooms)) return;
      rooms.forEach((room) => {
        const id = room?.room_id ?? room?.id;
        const number = room?.room_number ?? room?.roomNumber ?? room?.name;
        if (id != null && !number) labels[String(id)] = t("Room {number}", { number: id });
      });
    });
    return labels;
  }, [roomNumberById, roomNumbersByCategory, t]);

  const gridDisplayReservations = useMemo(
    () => (usingRoomNumbers ? expandReservationsByRoomAssignmentSegments(effectiveReservations) : effectiveReservations),
    [effectiveReservations, usingRoomNumbers]
  );

  const getLiveAvailableRoomsForPanelItem = useCallback(
    (panelItem, rooms, assignments = optimisticAssignmentsByReservationId) => {
      if (!panelItem || !Array.isArray(rooms) || rooms.length === 0) return [];
      const occupiedRoomIds = new Set();
      (Array.isArray(reservations) ? reservations : []).forEach((reservation) => {
        if (String(reservation?.id) === String(panelItem.reservationId) || isNonOccupyingReservation(reservation)) return;
        const optimisticAssignment = assignments[String(reservation?.id)];
        const segments = optimisticAssignment
          ? [{ ...reservation, ...optimisticAssignment }]
          : buildReservationRoomAssignmentSegments(reservation);
        segments.forEach((segment) => {
          if (reservationsOverlapByRange(panelItem.reservation, segment)) {
            const roomId = getReservationRoomId(segment);
            if (roomId) occupiedRoomIds.add(roomId);
          }
        });
      });
      (Array.isArray(roomBlocks) ? roomBlocks : []).forEach((block) => {
        if (reservationsOverlapByRange(panelItem.reservation, {
          check_in_date: normalizeISODate(block?.start_date),
          check_out_date: addDaysISO(block?.end_date, 1),
        })) {
          occupiedRoomIds.add(String(block?.room_id ?? block?.room?.id ?? ""));
        }
      });
      return rooms.filter((room) => !occupiedRoomIds.has(String(room?.id ?? room?.room_id ?? "")));
    },
    [reservations, roomBlocks, optimisticAssignmentsByReservationId]
  );
  const liveAvailabilityRef = useRef(getLiveAvailableRoomsForPanelItem);
  liveAvailabilityRef.current = getLiveAvailableRoomsForPanelItem;

  const liveRoomsByReservation = useMemo(() => {
    const result = {};
    (unassignedPanel?.items || []).forEach((item) => {
      result[item.reservationId] = getLiveAvailableRoomsForPanelItem(
        item, availableRoomsByReservation[item.reservationId]
      );
    });
    return result;
  }, [unassignedPanel, availableRoomsByReservation, getLiveAvailableRoomsForPanelItem]);

  const proposedRoomByReservation = useMemo(() => getDistinctRoomProposals({
    items: unassignedPanel?.items || [],
    availableRoomsByReservation: liveRoomsByReservation,
    preferredRoomByReservation: selectedRoomByReservation,
    priorityReservationId: priorityRoomReservationId,
  }), [unassignedPanel, liveRoomsByReservation, selectedRoomByReservation, priorityRoomReservationId]);

  useEffect(() => {
    setSelectedRoomByReservation((previous) => {
      const ids = Object.keys(proposedRoomByReservation);
      return ids.length === Object.keys(previous).length &&
        ids.every((id) => previous[id] === proposedRoomByReservation[id])
        ? previous
        : proposedRoomByReservation;
    });
  }, [proposedRoomByReservation]);

  useEffect(() => {
    if (!usingRoomNumbers) return;
    if (!Array.isArray(reservations) || reservations.length === 0) return;

    setOptimisticAssignmentsByReservationId((prev) => {
      const keys = Object.keys(prev || {});
      if (keys.length === 0) return prev;

      const byReservationId = new Map();
      reservations.forEach((reservation) => {
        if (reservation?.id == null) return;
        byReservationId.set(String(reservation.id), reservation);
      });

      let changed = false;
      const next = { ...prev };

      keys.forEach((reservationId) => {
        const optimisticAssignment = prev[reservationId];
        const liveReservation = byReservationId.get(reservationId);
        if (!liveReservation || !optimisticAssignment) return;

        const liveRoomId =
          liveReservation?.effective_room_id != null
            ? String(liveReservation.effective_room_id)
            : liveReservation?.room_id != null
              ? String(liveReservation.room_id)
              : liveReservation?.effective_room?.id != null
                ? String(liveReservation.effective_room.id)
                : liveReservation?.room?.id != null
                  ? String(liveReservation.room.id)
                  : "";
        const optimisticHasRoom = optimisticAssignment?.room_id != null;
        const optimisticRoomId = optimisticHasRoom ? String(optimisticAssignment.room_id) : "";

        const syncForAssigned = optimisticHasRoom && liveRoomId && liveRoomId === optimisticRoomId;
        const syncForUnassigned = !optimisticHasRoom && !liveRoomId;

        if (syncForAssigned || syncForUnassigned) {
          delete next[reservationId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [reservations, usingRoomNumbers, setOptimisticAssignmentsByReservationId]);

  const categories = useMemo(() => {
    const reservationsList = Array.isArray(gridDisplayReservations) ? gridDisplayReservations : [];
    const blocksList = Array.isArray(roomBlocks) ? roomBlocks : [];
    const visibleRangeStart = visibleDates[0] || "";
    const visibleRangeEndExclusive =
      visibleDates.length > 0 ? addDaysISO(visibleDates[visibleDates.length - 1], 1) : "";
    const byCategory = {};

    const overlapsVisibleRange = (reservation) => {
      if (!visibleRangeStart || !visibleRangeEndExclusive) return false;
      const inDate = normalizeISODate(reservation?.check_in_date);
      const outDate = normalizeISODate(reservation?.check_out_date);
      if (!inDate || !outDate) return false;
      return inDate < visibleRangeEndExclusive && outDate > visibleRangeStart;
    };

    const visibleReservations = reservationsList.filter(overlapsVisibleRange);

    const normalizedBlocks = blocksList
      .map((block) => {
        const startIso = normalizeISODate(block?.start_date);
        const endIso = normalizeISODate(block?.end_date);
        const endParsed = parseISO(endIso);
        const endExclusive = endParsed ? toISODateString(addDays(endParsed, 1)) : "";

        const roomCategoryId =
          block?.room?.room_category_id ??
          block?.room_category_id ??
          block?.room?.RoomCategory?.id ??
          null;

        const roomCategoryName = block?.room?.RoomCategory?.name ?? block?.room_category_name ?? null;

        if (!startIso || !endExclusive || roomCategoryId == null) return null;

        return {
          __type: "room_block",
          block_id: block?.id,
          room_id: block?.room_id ?? block?.room?.id ?? null,
          room_number: block?.room?.room_number ?? null,
          check_in_date: startIso,
          check_out_date: endExclusive,
          room_category_id: roomCategoryId,
          room_category: {
            id: roomCategoryId,
            name: roomCategoryName || null,
          },
          waitlist_entries: Array.isArray(block?.waitlist_entries) ? block.waitlist_entries : [],
        };
      })
      .filter(Boolean)
      .filter(overlapsVisibleRange);

    const merged = [...visibleReservations, ...normalizedBlocks];

    for (const reservation of merged) {
      const categoryId =
        reservation?.room_category_id ??
        reservation?.room_category?.id ??
        reservation?.room?.room_category_id ??
        null;

      const categoryName = reservation?.room_category?.name ?? reservation?.room_category_name ?? null;
      if (categoryId == null && categoryName == null) continue;

      const finalCategoryId = categoryId != null ? String(categoryId) : `name-only-${String(categoryName || "unknown")}`;
      const finalCategoryName = categoryName != null ? String(categoryName) : "";

      if (!byCategory[finalCategoryId]) {
        const roomCount = Number(
          roomCountsByCategory?.[finalCategoryId] ?? roomCountsByCategory?.[Number(finalCategoryId)] ?? 0
        );

        byCategory[finalCategoryId] = {
          categoryId: finalCategoryId,
          categoryName: finalCategoryName,
          reservations: [],
          roomCount,
        };
      }

      byCategory[finalCategoryId].reservations.push(reservation);
    }

    Object.keys(roomCountsByCategory || {}).forEach((rawCategoryKey) => {
      const categoryKey = String(rawCategoryKey);
      const roomCount = Number(roomCountsByCategory?.[rawCategoryKey] ?? 0);
      if (!Number.isFinite(roomCount) || roomCount <= 0) return;

      const categoryName =
        roomCategoryNamesById?.[categoryKey] ??
        roomCategoryNamesById?.[Number(categoryKey)] ??
        "";

      if (!byCategory[categoryKey]) {
        byCategory[categoryKey] = {
          categoryId: categoryKey,
          categoryName: String(categoryName),
          reservations: [],
          roomCount,
        };
        return;
      }

      byCategory[categoryKey].roomCount = roomCount;
      if (!byCategory[categoryKey].categoryName && categoryName) {
        byCategory[categoryKey].categoryName = String(categoryName);
      }
    });

    Object.keys(roomNumbersByCategory || {}).forEach((rawCategoryKey) => {
      const categoryKey = String(rawCategoryKey);
      const categoryRooms = Array.isArray(roomNumbersByCategory?.[rawCategoryKey])
        ? roomNumbersByCategory[rawCategoryKey]
        : [];
      const roomCountFromNumbers = categoryRooms.length;
      if (roomCountFromNumbers <= 0) return;

      const categoryName =
        roomCategoryNamesById?.[categoryKey] ??
        roomCategoryNamesById?.[Number(categoryKey)] ??
        "";

      if (!byCategory[categoryKey]) {
        byCategory[categoryKey] = {
          categoryId: categoryKey,
          categoryName: String(categoryName),
          reservations: [],
          roomCount: roomCountFromNumbers,
        };
        return;
      }

      if (Number(byCategory[categoryKey].roomCount || 0) <= 0) {
        byCategory[categoryKey].roomCount = roomCountFromNumbers;
      }
      if (!byCategory[categoryKey].categoryName && categoryName) {
        byCategory[categoryKey].categoryName = String(categoryName);
      }
    });

    return Object.values(byCategory).sort((a, b) =>
      String(a.categoryName || `Category ${a.categoryId}`).localeCompare(String(b.categoryName || `Category ${b.categoryId}`))
    );
  }, [
    gridDisplayReservations,
    roomBlocks,
    roomCountsByCategory,
    roomCategoryNamesById,
    roomNumbersByCategory,
    visibleDates,
  ]);

  const categoriesWithRangeData = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        visibleReservations: category.reservations,
      })),
    [categories]
  );

  const overbookedCategoryCount = useMemo(() => {
    if (visibleDates.length === 0 || categoriesWithRangeData.length === 0) return 0;

    let count = 0;
    for (const category of categoriesWithRangeData) {
      const configuredRooms = Number(category?.roomCount ?? 0);
      const baseRooms = configuredRooms > 0 ? configuredRooms : 1;
      const lanes = buildFixedLanesForCategory(category.visibleReservations, baseRooms);
      const overbookedDates = buildOverbookedDateSetForCategory(
        category.visibleReservations,
        visibleDates,
        configuredRooms
      );
      if (lanes.length > baseRooms || overbookedDates.size > 0) count += 1;
    }
    return count;
  }, [categoriesWithRangeData, visibleDates]);

  const selectedRangeLabel =
    visibleDates.length > 0
      ? `${toCalendarDateLabel(visibleDates[0], locale)} — ${toCalendarDateLabel(visibleDates[visibleDates.length - 1], locale)} · ${t(visibleDates.length === 1 ? "{count} day" : "{count} days", { count: visibleDates.length })}`
      : t("No dates selected");

  const totalRowsLoaded =
    effectiveReservations.length + (Array.isArray(roomBlocks) ? roomBlocks.length : 0);

  const visibleRowsLoaded = categoriesWithRangeData.reduce(
    (accumulator, category) => accumulator + category.visibleReservations.length,
    0
  );

  const overbookingSummary =
    overbookedCategoryCount > 0
      ? t(overbookedCategoryCount === 1 ? "{count} category with overbooking in selected range" : "{count} categories with overbooking in selected range", { count: overbookedCategoryCount })
      : t("No overbooking in selected range");
  const queuedUpdatesSummary =
    pendingMutationCount > 0 ? t(pendingMutationCount === 1 ? "{count} room assignment update queued" : "{count} room assignment updates queued", { count: pendingMutationCount }) : "";

  const updateRoomMoveConnectors = useCallback(() => {
    const wrap = tableWrapRef.current;
    const plane = tablePlaneRef.current;
    if (!wrap || !plane || !usingRoomNumbers) {
      setRoomMoveOverlay((prev) =>
        prev.width === 0 && prev.height === 0 && prev.connectors.length === 0
          ? prev
          : { width: 0, height: 0, connectors: [] }
      );
      return;
    }

    const planeRect = plane.getBoundingClientRect();
    const scaleX = plane.offsetWidth > 0 ? planeRect.width / plane.offsetWidth : 1;
    const scaleY = plane.offsetHeight > 0 ? planeRect.height / plane.offsetHeight : 1;
    const safeScaleX = Number.isFinite(scaleX) && scaleX > 0 ? scaleX : 1;
    const safeScaleY = Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 1;
    const inNodes = Array.from(plane.querySelectorAll("[data-room-move-in-key]"));
    const outNodes = Array.from(plane.querySelectorAll("[data-room-move-out-key]"));
    const inByKey = new Map(
      inNodes
        .map((node) => [node.getAttribute("data-room-move-in-key"), node])
        .filter(([key, node]) => key && node)
    );

    const connectors = outNodes
      .map((outNode) => {
        const key = outNode.getAttribute("data-room-move-out-key");
        const inNode = key ? inByKey.get(key) : null;
        if (!key || !inNode) return null;

        const outRect = outNode.getBoundingClientRect();
        const inRect = inNode.getBoundingClientRect();
        const x1 = (outRect.right - planeRect.left) / safeScaleX;
        const y1 = (outRect.top + outRect.height / 2 - planeRect.top) / safeScaleY;
        const x2 = (inRect.left - planeRect.left) / safeScaleX;
        const y2 = (inRect.top + inRect.height / 2 - planeRect.top) / safeScaleY;

        if (![x1, y1, x2, y2].every(Number.isFinite)) return null;

        return {
          key,
          x1: Number(x1.toFixed(1)),
          y1: Number(y1.toFixed(1)),
          x2: Number(x2.toFixed(1)),
          y2: Number(y2.toFixed(1)),
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.key).localeCompare(String(b.key)));

    const nextOverlay = {
      width: Math.max(plane.scrollWidth, plane.clientWidth, wrap.clientWidth, 1),
      height: Math.max(plane.scrollHeight, plane.clientHeight, wrap.clientHeight, 1),
      connectors,
    };
    const serialize = (overlay) =>
      `${overlay.width}x${overlay.height}|${overlay.connectors
        .map((line) => `${line.key}:${line.x1},${line.y1},${line.x2},${line.y2}`)
        .join(";")}`;

    setRoomMoveOverlay((prev) => (serialize(prev) === serialize(nextOverlay) ? prev : nextOverlay));
  }, [usingRoomNumbers]);

  useLayoutEffect(() => {
    if (!usingRoomNumbers) {
      updateRoomMoveConnectors();
      return undefined;
    }

    let frameId = 0;
    const scheduleUpdate = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateRoomMoveConnectors);
    };

    scheduleUpdate();

    const wrap = tableWrapRef.current;
    window.addEventListener("resize", scheduleUpdate);
    wrap?.addEventListener("scroll", scheduleUpdate, { passive: true });

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleUpdate);
      wrap?.removeEventListener("scroll", scheduleUpdate);
    };
  }, [
    categoriesWithRangeData,
    gridDisplayReservations,
    updateRoomMoveConnectors,
    usingRoomNumbers,
    visibleDates,
  ]);

  const resetRange = () => {
    setRange(sanitizeRange(currentDateIso, addDaysISO(currentDateIso, initialVisibleDays - 1), initialVisibleDays, currentDateIso));
  };

  const setOperationError = (message) => {
    const finalMessage = String(message || "Operation failed.");
    setGridActionError(finalMessage);
    if (unassignedPanel) setPanelError(finalMessage);
  };

  const clearOperationError = () => {
    if (gridActionError) setGridActionError("");
    if (panelError) setPanelError("");
  };

  const setDragPayload = (payload, event) => {
    if (!canAssignRoomsRef.current) return;
    dragPayloadRef.current = payload;
    logDnd("setDragPayload", payload);
    if (!event?.dataTransfer) return;
    event.dataTransfer.effectAllowed = "move";
    try {
      event.dataTransfer.setData("application/x-palantir-room-dnd", JSON.stringify(payload));
    } catch (error) {
      // Ignore unsupported custom mime types.
    }
    if (payload?.reservationId) {
      try {
        event.dataTransfer.setData("text/plain", String(payload.reservationId));
      } catch (error) {
        // Ignore fallback errors.
      }
    }
  };

  const readDragPayload = (event) => {
    const custom = event?.dataTransfer?.getData?.("application/x-palantir-room-dnd");
    if (custom) {
      try {
        const parsed = JSON.parse(custom);
        if (parsed && parsed.kind && parsed.reservationId) {
          logDnd("readDragPayload:custom", parsed);
          return parsed;
        }
      } catch (error) {
        warnDnd("readDragPayload:custom_parse_error", error?.message || error);
      }
    }

    if (dragPayloadRef.current?.reservationId) {
      logDnd("readDragPayload:ref", dragPayloadRef.current);
      return dragPayloadRef.current;
    }

    const plain = event?.dataTransfer?.getData?.("text/plain");
    if (plain) {
      const reservationId = String(plain).trim();
      if (reservationId) {
        const payload = { kind: "legacy", reservationId };
        logDnd("readDragPayload:plain", payload);
        return payload;
      }
    }
    warnDnd("readDragPayload:none");
    return null;
  };

  const buildOptimisticRoomAssignment = (reservation, nextRoomId, fallbackCategoryId, fallbackRoomNumber) => {
    if (nextRoomId == null || Number(nextRoomId) <= 0) {
      return { room_id: null, effective_room_id: null, room: null, effective_room: null };
    }

    const numericRoomId = Number(nextRoomId);
    const roomNumber =
      fallbackRoomNumber ||
      roomNumberById?.[String(numericRoomId)] ||
      reservation?.effective_room?.room_number ||
      reservation?.room?.room_number ||
      reservation?.room_number ||
      `Room ${numericRoomId}`;
    const roomCategoryId =
      fallbackCategoryId ??
      reservation?.effective_room?.room_category_id ??
      reservation?.room_category_id ??
      reservation?.room?.room_category_id ??
      reservation?.room_category?.id ??
      null;

    const roomPayload = {
      id: numericRoomId,
      room_number: String(roomNumber),
      room_category_id: roomCategoryId,
    };

    return {
      room_id: numericRoomId,
      effective_room_id: numericRoomId,
      effective_room_number: String(roomNumber),
      room: roomPayload,
      effective_room: roomPayload,
    };
  };

  const applyOptimisticAssignments = (patchByReservationId) => {
    if (!canAssignRoomsRef.current) return;
    logDnd("applyOptimisticAssignments", patchByReservationId);
    setOptimisticAssignmentsByReservationId((prev) => ({
      ...prev,
      ...patchByReservationId,
    }));
  };

  const clearOptimisticAssignments = (reservationIds = []) => {
    logDnd("clearOptimisticAssignments", reservationIds);
    setOptimisticAssignmentsByReservationId((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      reservationIds.forEach((reservationId) => {
        const key = String(reservationId || "");
        if (!key || !Object.prototype.hasOwnProperty.call(next, key)) return;
        delete next[key];
        changed = true;
      });
      return changed ? next : prev;
    });
  };

  const runMutationQueue = useCallback(() => {
    if (!canAssignRoomsRef.current) return;
    if (mutationQueueRunningRef.current) return;
    const nextMutation = mutationQueueRef.current.shift();
    if (!nextMutation) return;

    mutationQueueRunningRef.current = true;
    void (async () => {
      try {
        await nextMutation.task();
        nextMutation.resolve();
      } catch (error) {
        nextMutation.reject(error);
      } finally {
        mutationQueueRunningRef.current = false;
        setPendingMutationCount((prev) => Math.max(0, prev - 1));
        runMutationQueue();
      }
    })();
  }, []);

  const enqueueMutation = useCallback(
    (task) =>
      new Promise((resolve, reject) => {
        if (!canAssignRoomsRef.current) {
          resolve(false);
          return;
        }
        mutationQueueRef.current.push({ task, resolve, reject });
        setPendingMutationCount((prev) => prev + 1);
        runMutationQueue();
      }),
    [runMutationQueue]
  );

  const canDropPayloadOnRoom = (payload, targetCategoryId, targetRoomId) => {
    if (!canAssignRoomsRef.current) return false;
    if (!payload?.reservationId || !targetCategoryId || !targetRoomId) return false;

    if (payload.kind === "unassigned_panel" || payload.kind === "legacy") {
      if (!unassignedPanel || String(unassignedPanel.categoryId) !== String(targetCategoryId)) return false;
      return unassignedPanel.items.some((item) => item.reservationId === String(payload.reservationId));
    }

    if (payload.kind === "assigned_room") {
      const payloadCategoryId = String(payload.categoryId || "");
      return Boolean(payloadCategoryId) && payloadCategoryId === String(targetCategoryId);
    }

    return false;
  };

  const canDropPayloadOnUnassigned = (payload, targetCategoryId) => {
    if (!canAssignRoomsRef.current) return false;
    if (!payload?.reservationId || !targetCategoryId) return false;
    if (payload.kind !== "assigned_room") return false;
    const payloadCategoryId = String(payload.categoryId || "");
    return Boolean(payloadCategoryId) && payloadCategoryId === String(targetCategoryId);
  };

  const buildCellsForLane = (rowContext, rowKey, laneItems, overbookedDates, overflowTooltipByDate, onDropTarget) => {
    const targetCategoryId = String(rowContext?.categoryId || "");
    const targetRoomId = String(rowContext?.roomId || "");
    const isDropEnabled = Boolean(canAssignRooms && usingRoomNumbers && targetCategoryId && targetRoomId);
    const rowDropKey = `${targetCategoryId}:${targetRoomId}`;

    const handleCellDragOver = (event) => {
      if (!isDropEnabled) return;
      const payload = readDragPayload(event);
      if (!canDropPayloadOnRoom(payload, targetCategoryId, targetRoomId)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dragOverTargetKey !== rowDropKey) setDragOverTargetKey(rowDropKey);
    };

    const handleCellDrop = (event, dateIso, targetReservation) => {
      if (!isDropEnabled || typeof onDropTarget !== "function") return;
      const payload = readDragPayload(event);
      if (!canDropPayloadOnRoom(payload, targetCategoryId, targetRoomId)) {
        warnDnd("handleCellDrop:blocked_by_canDrop", {
          payload,
          dateIso,
          targetCategoryId,
          targetRoomId,
          targetReservationId: targetReservation?.id ?? null,
        });
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragOverTargetKey("");
      logDnd("handleCellDrop:accepted", {
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
        targetReservationId: targetReservation?.id ?? null,
      });
      void onDropTarget({
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
        targetReservation: targetReservation || null,
      });
    };

    const segments = (Array.isArray(laneItems) ? laneItems : [])
      .map((laneItem) => {
        const reservation = laneItem?.reservation;
        const segment = segmentForVisibleWindow(reservation, visibleDates);
        if (!segment) return null;

        return {
          reservation,
          label: getReservationLabelForGrid(reservation),
          startIdx: segment.startIdx,
          span: segment.span,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.startIdx - b.startIdx || b.span - a.span);

    const cells = [];
    let cursor = 0;

    for (const [segmentIndex, segment] of segments.entries()) {
      let segmentStart = segment.startIdx;
      let segmentSpan = segment.span;

      if (segmentStart < cursor) {
        const trimmed = cursor - segmentStart;
        if (trimmed >= segmentSpan) continue;
        segmentStart = cursor;
        segmentSpan -= trimmed;
      }

      while (cursor < segmentStart) {
        const dateIso = visibleDates[cursor];
        const dayIsOverbooked = overbookedDates.has(dateIso);
        const overbookingTooltip = dayIsOverbooked ? overflowTooltipByDate[dateIso] : "";

        cells.push(
          <td
            key={`${rowKey}-empty-${cursor}`}
            className={`res-grid-day-cell${dateIso === currentDateIso ? " is-today" : ""}${
              dayIsOverbooked ? " is-overbook" : ""
            }`}
            title={overbookingTooltip || undefined}
            onDragOver={handleCellDragOver}
            onDrop={(event) => handleCellDrop(event, dateIso, null)}
          >
            <span className="res-grid-empty">—</span>
          </td>
        );
        cursor += 1;
      }

      const segmentDates = visibleDates.slice(segmentStart, segmentStart + segmentSpan);
      const segmentTooltips = segmentDates.map((dateIso) => overflowTooltipByDate[dateIso]).filter(Boolean);
      const segmentOverbookTooltip = Array.from(new Set(segmentTooltips)).join("\n");
      const segmentHasOverbook = segmentTooltips.length > 0;
      const segmentCellStyle = buildSegmentOverbookCellStyle(
        segmentStart,
        segmentSpan,
        visibleDates,
        overbookedDates,
        showDaySeparatorsInsideBar,
        currentDateIso
      );
      const movementSegment = segment.reservation?.__room_assignment_segment || null;
      const hasRoomMoveStart = Boolean(movementSegment?.moved_from_room_number);
      const hasRoomMoveEnd = Boolean(movementSegment?.moved_to_room_number);
      const roomMoveReservationId =
        segment.reservation?.id != null ? String(segment.reservation.id) : "";
      const roomMoveSegmentIndex = Number(movementSegment?.index);
      const roomMoveInKey =
        hasRoomMoveStart && roomMoveReservationId && Number.isFinite(roomMoveSegmentIndex)
          ? `${roomMoveReservationId}:${roomMoveSegmentIndex}`
          : "";
      const roomMoveOutKey =
        hasRoomMoveEnd && roomMoveReservationId && Number.isFinite(roomMoveSegmentIndex)
          ? `${roomMoveReservationId}:${roomMoveSegmentIndex + 1}`
          : "";
      const moveTooltip = [
        hasRoomMoveStart
          ? t("Moved from {room} on {date}", { room: movementSegment.moved_from_room_number, date: toDayLabel(movementSegment.date_from, locale) })
          : "",
        hasRoomMoveEnd
          ? t("Moves to {room} on {date}", { room: movementSegment.moved_to_room_number, date: toDayLabel(movementSegment.date_to, locale) })
          : "",
      ]
        .filter(Boolean)
        .join("\n");
      const isHighlighted =
        highlightReservationId &&
        segment.reservation?.id != null &&
        String(segment.reservation.id) === String(highlightReservationId);
      const isInHouseSegment = isInHouseReservation(segment.reservation);

      const clickable = typeof onOpenReservation === "function"
        && (!readOnly || segment.reservation?.__type !== "room_block");
      const canDragAssigned = Boolean(
        canAssignRooms && usingRoomNumbers &&
          segment.reservation?.__type !== "room_block" &&
          segment.reservation?.id != null &&
          getReservationRoomId(segment.reservation)
      );
      const buttonClass = [
        "res-grid-bar",
        segment.reservation?.__type === "room_block" ? "is-room-block" : "",
        getWaitlistEntryForBlock(segment.reservation) ? "is-waitlist-block" : "",
        isInHouseSegment ? "is-in-house" : "",
        !isInHouseSegment && segment.reservation?.__type !== "room_block" && segmentIndex % 2 === 1 ? "is-alt-tone" : "",
        movementSegment ? "is-moved-segment" : "",
        hasRoomMoveStart ? "has-room-move-start" : "",
        hasRoomMoveEnd ? "has-room-move-end" : "",
        isHighlighted ? "is-highlighted" : "",
        segmentHasOverbook ? "is-overbook" : "",
      ]
        .filter(Boolean)
        .join(" ");

      cells.push(
        <td
          key={`${rowKey}-seg-${segmentStart}-${segmentIndex}`}
          colSpan={segmentSpan}
          className={`res-grid-day-cell has-bar${segmentHasOverbook ? " is-overbook-segment" : ""}`}
          style={segmentCellStyle}
          title={segmentOverbookTooltip || undefined}
          onDragOver={handleCellDragOver}
          onDrop={(event) => handleCellDrop(event, visibleDates[segmentStart], segment.reservation)}
        >
          <div className="res-grid-bar-clip">
            <button
              type="button"
              className={buttonClass}
              disabled={!clickable && !canDragAssigned}
              draggable={canDragAssigned}
              onDragStart={(event) => {
                if (!canDragAssigned) return;
                const reservationId = String(segment.reservation.id);
                const payload = {
                  kind: "assigned_room",
                  reservationId,
                  categoryId: getReservationCategoryId(segment.reservation),
                  sourceRoomId: getReservationRoomId(segment.reservation),
                };
                logDnd("dragStart:assigned", {
                  reservationId,
                  payload,
                  label: segment.label,
                });
                clearOperationError();
                setDraggedReservationId(reservationId);
                setDragPayload(payload, event);
              }}
              onDragEnd={() => {
                logDnd("dragEnd:assigned", { reservationId: segment.reservation?.id ?? null });
                setDraggedReservationId("");
                setDragOverTargetKey("");
                dragPayloadRef.current = null;
              }}
              onClick={() => {
                if (!clickable) return;
                onOpenReservation(segment.reservation);
              }}
              title={
                segmentOverbookTooltip ||
                moveTooltip ||
                (segment.reservation?.__type === "room_block"
                  ? getWaitlistEntryForBlock(segment.reservation)
                    ? segment.label
                    : t("Room block")
                : t("Reservation"))
              }
            >
              {hasRoomMoveStart ? (
                <span
                  className="res-grid-roomMoveChip res-grid-roomMoveChip--start"
                  data-room-move-in-key={roomMoveInKey || undefined}
                  title={t("Moved from room {room}", { room: movementSegment.moved_from_room_number })}
                >
                  {t("IN")}
                </span>
              ) : null}
              <span className="res-grid-barLabel">{segment.label}</span>
              {hasRoomMoveEnd ? (
                <span
                  className="res-grid-roomMoveChip res-grid-roomMoveChip--end"
                  data-room-move-out-key={roomMoveOutKey || undefined}
                  title={t("Moves to room {room}", { room: movementSegment.moved_to_room_number })}
                >
                  {t("OUT")}
                </span>
              ) : null}
            </button>
          </div>
        </td>
      );

      cursor = segmentStart + segmentSpan;
    }

    while (cursor < visibleDates.length) {
      const dateIso = visibleDates[cursor];
      const dayIsOverbooked = overbookedDates.has(dateIso);
      const overbookingTooltip = dayIsOverbooked ? overflowTooltipByDate[dateIso] : "";

      cells.push(
        <td
          key={`${rowKey}-empty-${cursor}`}
          className={`res-grid-day-cell${dateIso === currentDateIso ? " is-today" : ""}${
            dayIsOverbooked ? " is-overbook" : ""
          }`}
          title={overbookingTooltip || undefined}
          onDragOver={handleCellDragOver}
          onDrop={(event) => handleCellDrop(event, dateIso, null)}
        >
          <span className="res-grid-empty">—</span>
        </td>
      );
      cursor += 1;
    }

    return cells;
  };

  const closeUnassignedPanel = () => {
    panelLoadSeqRef.current += 1;
    setUnassignedPanel(null);
    setPanelLoading(false);
    setPanelError("");
    setAvailableRoomsByReservation({});
    setSelectedRoomByReservation({});
    setPriorityRoomReservationId("");
    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
  };

  const loadAvailabilityForPanelItems = async (panelItems, requestSeq) => {
    if (readOnly) return;
    if (typeof onFetchAvailableRooms !== "function" || !Array.isArray(panelItems) || panelItems.length === 0) return;

    const resolvedPairs = await Promise.all(
      panelItems.map(async (item) => {
        try {
          const rows = await onFetchAvailableRooms(item.reservation);
          const validRows = Array.isArray(rows) ? rows : [];
          return [item.reservationId, validRows];
        } catch (error) {
          return [item.reservationId, []];
        }
      })
    );

    if (panelLoadSeqRef.current !== requestSeq) return;

    const availableByReservation = {};
    resolvedPairs.forEach(([reservationId, rooms]) => {
      availableByReservation[reservationId] = rooms;
    });

    setAvailableRoomsByReservation(availableByReservation);
  };

  const openUnassignedPanel = async (categoryId, categoryName, dateIso, reservationsForDay = []) => {
    const panelItems = (Array.isArray(reservationsForDay) ? reservationsForDay : [])
      .filter((reservation) => reservation && reservation?.id && reservation?.__type !== "room_block")
      .map((reservation) => ({
        reservationId: String(reservation.id),
        reservation,
      }));

    if (panelItems.length === 0) return;

    const requestSeq = panelLoadSeqRef.current + 1;
    panelLoadSeqRef.current = requestSeq;

    setPanelError("");
    setPanelLoading(true);
    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
    setAvailableRoomsByReservation({});
    setSelectedRoomByReservation({});
    setUnassignedPanel({
      categoryId: String(categoryId),
      categoryName: String(categoryName || ""),
      dateIso,
      items: panelItems,
    });
    setPriorityRoomReservationId("");

    try {
      await loadAvailabilityForPanelItems(panelItems, requestSeq);
    } catch (error) {
      if (panelLoadSeqRef.current === requestSeq) {
        setPanelError("Could not load available room numbers for one or more reservations.");
      }
    } finally {
      if (panelLoadSeqRef.current === requestSeq) setPanelLoading(false);
    }
  };

  const ensureAvailabilityForReservation = async (panelItem) => {
    if (!canAssignRoomsRef.current) return [];
    const reservationId = panelItem?.reservationId;
    if (!reservationId) return [];

    const cached = availableRoomsByReservation?.[reservationId];
    if (Array.isArray(cached) && cached.length >= 0) {
      if (cached.length > 0) return cached;
      if (typeof onFetchAvailableRooms !== "function") return [];
    }

    if (typeof onFetchAvailableRooms !== "function") return [];

    const rows = await onFetchAvailableRooms(panelItem.reservation);
    const validRows = Array.isArray(rows) ? rows : [];
    setAvailableRoomsByReservation((prev) => ({ ...prev, [reservationId]: validRows }));
    return validRows;
  };

  const assignPanelReservation = async (panelItem, targetRoomId) => {
    if (!canAssignRoomsRef.current) return false;
    if (!panelItem || !targetRoomId) return false;
    if (typeof onAssignRoom !== "function") {
      warnDnd("assignPanelReservation:no_onAssignRoom", {
        reservationId: panelItem?.reservationId ?? null,
        targetRoomId,
      });
      setPanelError("Room assignment is not available in this context.");
      return false;
    }

    const reservationId = panelItem.reservationId;
    const roomIdAsString = String(targetRoomId);
    let roomsSnapshotForReservation = [];

    setPanelError("");
    logDnd("assignPanelReservation:start", {
      reservationId,
      targetRoomId,
      reservation: panelItem?.reservation || null,
    });

    try {
      const cachedRooms = await ensureAvailabilityForReservation(panelItem);
      if (!canAssignRoomsRef.current) return false;
      if (optimisticAssignmentsRef.current[reservationId]?.room_id != null) return false;
      roomsSnapshotForReservation = cachedRooms;
      const availableRooms = liveAvailabilityRef.current(panelItem, cachedRooms, optimisticAssignmentsRef.current);
      const targetRoom = availableRooms.find((room) => String(room?.id ?? room?.room_id) === roomIdAsString);
      if (!targetRoom) {
        warnDnd("assignPanelReservation:target_not_available", {
          reservationId,
          targetRoomId,
          availableRooms,
        });
        setPanelError("That room is not available for the full reservation range.");
        return false;
      }

      const numericRoomId = Number(targetRoomId);
      const roomNumberLabel =
        targetRoom?.room_number ?? targetRoom?.number ?? targetRoom?.name ?? `Room ${numericRoomId}`;
      const roomCategoryId =
        targetRoom?.room_category_id ??
        panelItem?.reservation?.room_category_id ??
        panelItem?.reservation?.room?.room_category_id ??
        null;

      // Optimistic UI: move the bar immediately, then sync with backend.
      setOptimisticAssignmentsByReservationId((prev) => ({
        ...prev,
        [reservationId]: buildOptimisticRoomAssignment(
          panelItem?.reservation || null,
          numericRoomId,
          roomCategoryId,
          roomNumberLabel
        ),
      }));
      setUnassignedPanel((prevState) => {
        if (!prevState) return null;
        const remainingItems = prevState.items.filter((item) => item.reservationId !== reservationId);
        if (remainingItems.length === 0) return null;
        return { ...prevState, items: remainingItems };
      });
      setAvailableRoomsByReservation((prev) => {
        const next = { ...prev };
        delete next[reservationId];
        return next;
      });
      setSelectedRoomByReservation((prev) => {
        const next = { ...prev };
        delete next[reservationId];
        return next;
      });

      void enqueueMutation(async () => {
        logDnd("assignPanelReservation:enqueueMutation:task_start", {
          reservationId,
          targetRoomId: numericRoomId,
        });
        await onAssignRoom(panelItem.reservation, numericRoomId);
        logDnd("assignPanelReservation:enqueueMutation:task_success", {
          reservationId,
          targetRoomId: numericRoomId,
        });
      }).catch((error) => {
        warnDnd("assignPanelReservation:enqueueMutation:task_error", {
          reservationId,
          targetRoomId: numericRoomId,
          error: error?.message || error,
        });
        setOptimisticAssignmentsByReservationId((prev) => {
          if (!prev?.[reservationId]) return prev;
          const next = { ...prev };
          delete next[reservationId];
          return next;
        });

        setUnassignedPanel((prevState) => {
          const candidate =
            prevState && panelItem && !prevState.items.some((item) => item.reservationId === reservationId)
              ? { ...prevState, items: [panelItem, ...(prevState.items || [])] }
              : prevState;

          if (candidate) return candidate;

          const fallbackCategoryId =
            panelItem?.reservation?.room_category_id ??
            panelItem?.reservation?.room_category?.id ??
            "";
          const fallbackCategoryName =
            panelItem?.reservation?.room_category?.name ??
            unassignedPanel?.categoryName ??
            `Category ${fallbackCategoryId}`;

          return {
            categoryId: String(fallbackCategoryId || unassignedPanel?.categoryId || ""),
            categoryName: String(fallbackCategoryName),
            dateIso: String(unassignedPanel?.dateIso || range.start || ""),
            items: [panelItem],
          };
        });
        setAvailableRoomsByReservation((prev) => ({
          ...prev,
          [reservationId]:
            Array.isArray(prev?.[reservationId]) && prev[reservationId].length > 0
              ? prev[reservationId]
              : Array.isArray(roomsSnapshotForReservation)
                ? roomsSnapshotForReservation
                : [],
        }));
        setSelectedRoomByReservation((prev) => ({
          ...prev,
          [reservationId]: prev?.[reservationId] || roomIdAsString,
        }));

        const message =
          error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Could not assign this room.";
        setPanelError(String(message));
      });

      return true;
    } catch (error) {
      warnDnd("assignPanelReservation:prepare_error", {
        reservationId,
        targetRoomId,
        error: error?.message || error,
      });
      const message =
        error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Could not prepare this assignment.";
      setPanelError(String(message));
      return false;
    } finally {
      setDraggedReservationId("");
      setDragOverTargetKey("");
      dragPayloadRef.current = null;
    }
  };

  const resolveReservationForPayload = (payload) => {
    const reservationId = String(payload?.reservationId || "");
    if (!reservationId) return null;
    return reservationById.get(reservationId) || null;
  };

  const handleAssignedDropToRoom = async ({
    payload,
    dateIso,
    targetCategoryId,
    targetRoomId,
    targetReservation,
  }) => {
    if (!canAssignRoomsRef.current) return;
    if (typeof onAssignRoom !== "function") {
      warnDnd("handleAssignedDropToRoom:no_onAssignRoom", {
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
      });
      setOperationError("Room assignment is not available in this context.");
      return;
    }
    const draggedReservation = resolveReservationForPayload(payload);
    if (!draggedReservation || !targetRoomId) {
      warnDnd("handleAssignedDropToRoom:missing_target_or_reservation", {
        payload,
        resolvedReservationId: draggedReservation?.id ?? null,
        targetRoomId,
      });
      return;
    }

    logDnd("handleAssignedDropToRoom:start", {
      payload,
      draggedReservationId: draggedReservation.id,
      dateIso,
      targetCategoryId,
      targetRoomId,
      targetReservationId: targetReservation?.id ?? null,
    });

    if (draggedReservation?.__type === "room_block") {
      warnDnd("handleAssignedDropToRoom:dragged_room_block", { draggedReservation });
      return;
    }

    const draggedReservationId = String(draggedReservation.id);
    const sourceCategoryId = payload?.categoryId ? String(payload.categoryId) : getReservationCategoryId(draggedReservation);
    const sourceRoomId = payload?.sourceRoomId ? String(payload.sourceRoomId) : getReservationRoomId(draggedReservation);

    if (!sourceCategoryId || sourceCategoryId !== String(targetCategoryId)) {
      warnDnd("handleAssignedDropToRoom:category_mismatch", {
        draggedReservationId,
        sourceCategoryId,
        targetCategoryId,
      });
      setOperationError("You can only move reservations inside the same room category.");
      return;
    }

    if (targetReservation?.__type === "room_block") {
      warnDnd("handleAssignedDropToRoom:target_room_block", {
        draggedReservationId,
        targetReservation,
      });
      setOperationError("Cannot assign a reservation to a blocked room.");
      return;
    }

    const sameRoomDrop = sourceRoomId && String(sourceRoomId) === String(targetRoomId);
    let targetRealReservation =
      targetReservation && targetReservation?.id != null && targetReservation?.__type !== "room_block"
        ? resolveReservationForPayload({ reservationId: targetReservation.id }) || targetReservation
        : null;

    if (targetRealReservation && String(targetRealReservation.id) === draggedReservationId) {
      warnDnd("handleAssignedDropToRoom:self_drop", {
        draggedReservationId,
        targetReservationId: targetRealReservation.id,
      });
      return;
    }

    if (sameRoomDrop) {
      warnDnd("handleAssignedDropToRoom:same_room_drop", {
        draggedReservationId,
        sourceRoomId,
        targetRoomId,
      });
      return;
    }

    clearOperationError();

    const optimisticPatch = {};
    const rollbackReservationIds = [draggedReservationId];
    optimisticPatch[draggedReservationId] = buildOptimisticRoomAssignment(
      draggedReservation,
      Number(targetRoomId),
      targetCategoryId,
      roomNumberById?.[String(targetRoomId)]
    );

    const isInHouseMove = String(draggedReservation?.status || "").trim().toLowerCase() === "checked in";
    if (!isInHouseMove) {
      applyOptimisticAssignments(optimisticPatch);
    }

    void enqueueMutation(async () => {
      logDnd("handleAssignedDropToRoom:enqueueMutation:task_start", {
        draggedReservationId,
        targetRoomId: Number(targetRoomId),
      });
      await onAssignRoom(draggedReservation, Number(targetRoomId), { effectiveStayDate: dateIso });
      logDnd("handleAssignedDropToRoom:enqueueMutation:task_success", {
        draggedReservationId,
        targetRoomId: Number(targetRoomId),
      });
    }).catch(async (error) => {
      warnDnd("handleAssignedDropToRoom:enqueueMutation:task_error", {
        draggedReservationId,
        targetRoomId: Number(targetRoomId),
        error: error?.message || error,
      });
      clearOptimisticAssignments(rollbackReservationIds);
      setOperationError(error?.message || "Could not move reservation.");
    });

    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
  };

  const handleAssignedDropToUnassigned = async ({ payload, targetCategoryId, dateIso }) => {
    if (!canAssignRoomsRef.current) return;
    if (typeof onAssignRoom !== "function") {
      warnDnd("handleAssignedDropToUnassigned:no_onAssignRoom", {
        payload,
        targetCategoryId,
        dateIso,
      });
      setOperationError("Room assignment is not available in this context.");
      return;
    }
    const draggedReservation = resolveReservationForPayload(payload);
    if (!draggedReservation) {
      warnDnd("handleAssignedDropToUnassigned:missing_reservation", { payload });
      return;
    }
    if (draggedReservation?.__type === "room_block") {
      warnDnd("handleAssignedDropToUnassigned:dragged_room_block", { draggedReservation });
      return;
    }

    const draggedReservationId = String(draggedReservation.id);
    const sourceCategoryId = getReservationCategoryId(draggedReservation);
    if (!sourceCategoryId || sourceCategoryId !== String(targetCategoryId)) {
      warnDnd("handleAssignedDropToUnassigned:category_mismatch", {
        draggedReservationId,
        sourceCategoryId,
        targetCategoryId,
      });
      setOperationError("You can only move reservations inside the same room category.");
      return;
    }

    if (dateIso && !reservationOccupiesDate(draggedReservation, dateIso)) {
      warnDnd("handleAssignedDropToUnassigned:date_outside_range", {
        draggedReservationId,
        dateIso,
      });
      setOperationError("Drop on a day included in the reservation range.");
      return;
    }

    clearOperationError();

    applyOptimisticAssignments({
      [draggedReservationId]: buildOptimisticRoomAssignment(draggedReservation, null, sourceCategoryId, ""),
    });

    void enqueueMutation(async () => {
      logDnd("handleAssignedDropToUnassigned:enqueueMutation:task_start", {
        draggedReservationId,
      });
      await onAssignRoom(draggedReservation, null);
      logDnd("handleAssignedDropToUnassigned:enqueueMutation:task_success", {
        draggedReservationId,
      });

      if (unassignedPanel && String(unassignedPanel.categoryId) === String(targetCategoryId)) {
        const alreadyInPanel = unassignedPanel.items.some((item) => item.reservationId === draggedReservationId);
        if (!alreadyInPanel && reservationOccupiesDate(draggedReservation, dateIso)) {
          setUnassignedPanel((prev) =>
            prev
              ? {
                  ...prev,
                  items: [
                    {
                      reservationId: draggedReservationId,
                      reservation: draggedReservation,
                    },
                    ...prev.items,
                  ],
                }
              : prev
          );
        }
      }
    }).catch((error) => {
      warnDnd("handleAssignedDropToUnassigned:enqueueMutation:task_error", {
        draggedReservationId,
        error: error?.message || error,
      });
      clearOptimisticAssignments([draggedReservationId]);
      setOperationError(error?.message || "Could not unassign this reservation.");
    });

    setDraggedReservationId("");
    setDragOverTargetKey("");
    dragPayloadRef.current = null;
  };

  const handleDropOnRoomCell = async ({ payload, dateIso, targetCategoryId, targetRoomId, targetReservation }) => {
    if (!canAssignRoomsRef.current) return;
    if (!payload?.reservationId || !targetRoomId || !targetCategoryId) {
      warnDnd("handleDropOnRoomCell:missing_params", {
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
      });
      return;
    }
    if (!canDropPayloadOnRoom(payload, targetCategoryId, targetRoomId)) {
      warnDnd("handleDropOnRoomCell:blocked_by_canDrop", {
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
      });
      return;
    }
    logDnd("handleDropOnRoomCell:accepted", {
      payload,
      dateIso,
      targetCategoryId,
      targetRoomId,
      targetReservationId: targetReservation?.id ?? null,
    });

    if (payload.kind === "unassigned_panel" || payload.kind === "legacy") {
      const panelItem = unassignedPanel.items.find((item) => item.reservationId === String(payload.reservationId));
      if (!panelItem) {
        warnDnd("handleDropOnRoomCell:panel_item_not_found", {
          payload,
          targetCategoryId,
          targetRoomId,
        });
        return;
      }
      await assignPanelReservation(panelItem, targetRoomId);
      return;
    }

    if (payload.kind === "assigned_room") {
      await handleAssignedDropToRoom({
        payload,
        dateIso,
        targetCategoryId,
        targetRoomId,
        targetReservation,
      });
    }
  };

  const handleDropOnUnassignedCell = async ({ payload, dateIso, targetCategoryId }) => {
    if (!canAssignRoomsRef.current) return;
    if (!payload?.reservationId || !targetCategoryId) {
      warnDnd("handleDropOnUnassignedCell:missing_params", { payload, dateIso, targetCategoryId });
      return;
    }
    if (!canDropPayloadOnUnassigned(payload, targetCategoryId)) {
      warnDnd("handleDropOnUnassignedCell:blocked_by_canDrop", {
        payload,
        dateIso,
        targetCategoryId,
      });
      return;
    }
    logDnd("handleDropOnUnassignedCell:accepted", { payload, dateIso, targetCategoryId });
    await handleAssignedDropToUnassigned({ payload, targetCategoryId, dateIso });
  };

  const handleDropOnRoomRow = async (event, rowCategoryId, targetRoomId) => {
    if (!canAssignRoomsRef.current) return;
    if (event.defaultPrevented) return;
    const payload = readDragPayload(event);
    if (!payload?.reservationId || !targetRoomId) {
      warnDnd("handleDropOnRoomRow:missing_params", {
        payload,
        rowCategoryId,
        targetRoomId,
      });
      return;
    }
    if (!canDropPayloadOnRoom(payload, rowCategoryId, targetRoomId)) {
      warnDnd("handleDropOnRoomRow:blocked_by_canDrop", {
        payload,
        rowCategoryId,
        targetRoomId,
      });
      return;
    }
    event.preventDefault();
    logDnd("handleDropOnRoomRow:accepted", { payload, rowCategoryId, targetRoomId });
    await handleDropOnRoomCell({
      payload,
      dateIso: "",
      targetCategoryId: rowCategoryId,
      targetRoomId,
      targetReservation: null,
    });
  };

  return (
    <div className="rm-rates-card res-grid-card" lang={locale}>
      <div className="rm-rates-header">
        <div className="res-grid-headingRow">
          <div className="res-grid-heading">
            <span className="res-grid-titleIcon" aria-hidden="true">
              <FiGrid />
            </span>
            <div>
              <h3 className="rm-rates-title">{t("Occupancy Grid")}</h3>
              <div className="rm-rates-subtitle">{t("Room inventory and stays across the selected operating window")}</div>
            </div>
          </div>

          {typeof onBackToList === "function" ? (
            <button type="button" className="res-grid-backBtn" onClick={onBackToList}>
              <FiArrowLeft aria-hidden="true" />
              {t("Reservations")}
            </button>
          ) : null}
        </div>

        <div className="res-grid-insights" aria-label={t("Occupancy grid summary")}>
          <span className="res-grid-insight is-data">
            <FiGrid aria-hidden="true" />
            <strong>{visibleRowsLoaded}</strong> {t("of")} {totalRowsLoaded} {t("stays and blocks in range")}
          </span>
          <span className="res-grid-insight is-data">
            <FiLayers aria-hidden="true" />
            <strong>{categoriesWithRangeData.length}</strong> {t(categoriesWithRangeData.length === 1 ? "room category" : "room categories")}
          </span>
          <span className={`res-grid-insight${overbookedCategoryCount > 0 ? " is-warning" : " is-clear"}`}>
            {overbookedCategoryCount > 0 ? <FiAlertTriangle aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
            {overbookingSummary}
          </span>
          {queuedUpdatesSummary ? (
            <span className="res-grid-insight is-pending">
              <FiClock aria-hidden="true" />
              {queuedUpdatesSummary}
            </span>
          ) : null}
        </div>

        {gridActionError ? (
          <div className="res-grid-opError" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            {translateError(gridActionError)}
          </div>
        ) : null}
      </div>

      {readOnly ? (
        <p className="res-grid-floatHint" role="note">
          {t("Read only. You can explore dates and reservations; room assignment is disabled.")}
        </p>
      ) : null}

      {!isGridBootLoading ? (
        <>
          <div className="res-grid-toolbar">
            <div className="res-grid-viewControls">
              <span className="res-grid-controlLabel">{t("View")}</span>
              <div className="res-grid-modeSwitch" role="group" aria-label={t("Occupancy layout")}>
                <button
                  type="button"
                  className={`res-grid-modeBtn${!usingRoomNumbers ? " is-active" : ""}`}
                  aria-pressed={!usingRoomNumbers}
                  onClick={() => setViewMode("lanes")}
                >
                  <FiLayers aria-hidden="true" />
                  {t("Lanes")}
                </button>
                <button
                  type="button"
                  className={`res-grid-modeBtn${usingRoomNumbers ? " is-active" : ""}`}
                  aria-pressed={usingRoomNumbers}
                  onClick={() => setViewMode("room_numbers")}
                >
                  <FiHash aria-hidden="true" />
                  {t("Room numbers")}
                </button>
              </div>
            </div>

            <div className="res-grid-dateControls">
              <label className="res-grid-dateLabel">
                <FiCalendar aria-hidden="true" />
                {t("From")}
                <input
                  type="date"
                  className="res-grid-dateInput"
                  value={range.start}
                  onChange={(event) =>
                    setRange((prev) => sanitizeRange(event.target.value, prev.end || event.target.value, initialVisibleDays, currentDateIso))
                  }
                />
              </label>

              <label className="res-grid-dateLabel">
                {t("To")}
                <input
                  type="date"
                  className="res-grid-dateInput"
                  value={range.end}
                  onChange={(event) =>
                    setRange((prev) => sanitizeRange(prev.start || event.target.value, event.target.value, initialVisibleDays, currentDateIso))
                  }
                />
              </label>

              <span className="res-grid-range">{selectedRangeLabel}</span>

              <button type="button" className="res-grid-todayBtn" onClick={resetRange}>
                <FiRotateCcw aria-hidden="true" />
                {t("Today")}
              </button>
            </div>
          </div>

          <div className="res-grid-legend" aria-label={t("Occupancy grid legend")}>
            <span className="res-grid-legendTitle">{t("Legend")}</span>
            <span className="res-grid-legendItem">
              <span className="res-grid-legendSwatch is-reservation" aria-hidden="true" /> {t("Reservation")}
            </span>
            <span className="res-grid-legendItem">
              <span className="res-grid-legendSwatch is-in-house" aria-hidden="true" /> {t("In house")}
            </span>
            <span className="res-grid-legendItem">
              <span className="res-grid-legendSwatch is-block" aria-hidden="true" /> {t("Room block")}
            </span>
            <span className="res-grid-legendItem">
              <span className="res-grid-legendSwatch is-unassigned" aria-hidden="true" /> {t("Room not assigned")}
            </span>
            <span className="res-grid-legendItem">
              <span className="res-grid-legendSwatch is-overbook" aria-hidden="true" /> {t("Overbooking")}
            </span>
          </div>
        </>
      ) : null}

      {isGridBootLoading ? (
        <div className="res-grid-loadingBlock">
          <div className="res-grid-loadingRow">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span>{t("Loading occupancy grid...")}</span>
          </div>
        </div>
      ) : (
        <div className="rm-rates-table-wrap res-grid-table-wrap" ref={tableWrapRef}>
          {visibleDates.length === 0 ? (
            <div className="res-grid-empty-state">{t("Select a valid date range.")}</div>
          ) : categoriesWithRangeData.length === 0 ? (
            <div className="res-grid-empty-state">{t("No reservations found.")}</div>
          ) : (
            <div className="res-grid-scrollPlane" ref={tablePlaneRef}>
              {roomMoveOverlay.connectors.length > 0 ? (
                <svg
                  className="res-grid-roomMoveOverlay"
                  width={roomMoveOverlay.width}
                  height={roomMoveOverlay.height}
                  viewBox={`0 0 ${roomMoveOverlay.width} ${roomMoveOverlay.height}`}
                  aria-hidden="true"
                >
                  {roomMoveOverlay.connectors.map((line) => (
                    <line
                      key={line.key}
                      className="res-grid-roomMoveConnector"
                      x1={line.x1}
                      y1={line.y1}
                      x2={line.x2}
                      y2={line.y2}
                    />
                  ))}
                </svg>
              ) : null}

              <table className="rm-rates-table res-grid-table">
                <colgroup>
                  <col className="res-grid-col--category" />
                  <col className="res-grid-col--room" />
                  {visibleDates.map((dateIso) => (
                    <col key={dateIso} className="res-grid-col--day" />
                  ))}
                </colgroup>

                <thead>
                  <tr>
                    <th className="res-grid-th rm-sticky-1">
                      <div className="res-grid-thMain">{t("Room Category")}</div>
                      <div className="res-grid-thSub">{usingRoomNumbers ? t("Inventory") : t("Availability lanes")}</div>
                    </th>
                    <th className="res-grid-th rm-sticky-2">
                      <div className="res-grid-thMain">{t("Room")}</div>
                      <div className="res-grid-thSub">{usingRoomNumbers ? t("Number") : t("Lane")}</div>
                    </th>
                    {visibleDates.map((dateIso) => (
                      <th key={dateIso} className={`res-grid-th${dateIso === currentDateIso ? " is-today" : ""}`}>
                        <div className="res-grid-thMain">{toWeekdayLabel(dateIso, locale)}</div>
                        <div className="res-grid-thSub">{toCalendarDateLabel(dateIso, locale)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>

            <tbody>
              {categoriesWithRangeData.flatMap((category, categoryIdx) => {
                const configuredRooms = Number(category?.roomCount ?? 0);
                const baseRooms = configuredRooms > 0 ? configuredRooms : 1;
                const lanes = buildFixedLanesForCategory(category.visibleReservations, baseRooms);
                const overbookedDates = buildOverbookedDateSetForCategory(
                  category.visibleReservations,
                  visibleDates,
                  configuredRooms
                );

                const overflowTooltipByDate = {};
                if (configuredRooms > 0 && lanes.length > configuredRooms) {
                  visibleDates.forEach((dateIso) => {
                    const overflowLabels = [];

                    for (let laneIndex = configuredRooms; laneIndex < lanes.length; laneIndex += 1) {
                      for (const laneItem of lanes[laneIndex]) {
                        if (reservationOccupiesDate(laneItem?.reservation, dateIso)) {
                          overflowLabels.push(getReservationLabelForGrid(laneItem.reservation));
                        }
                      }
                    }

                    if (overflowLabels.length > 0) {
                      overflowTooltipByDate[dateIso] = buildOverflowTooltip(toDayLabel(dateIso, locale), overflowLabels, t);
                    }
                  });
                }

                const categoryRows = [];

                if (!usingRoomNumbers) {
                  lanes.forEach((laneItems, laneIdx) => {
                    const roomLabel =
                      configuredRooms > 0 && laneIdx >= configuredRooms
                        ? t("Overflow {number}", { number: laneIdx - configuredRooms + 1 })
                        : t("Room {number}", { number: laneIdx + 1 });

                    categoryRows.push({
                      rowKey: `${category.categoryId}-lane-${laneIdx}`,
                      laneItems,
                      roomId: "",
                      categoryId: String(category.categoryId),
                      categoryName: category.categoryName,
                      roomLabel,
                      isOverflow: configuredRooms > 0 && laneIdx >= configuredRooms,
                      isUnassignedSummary: false,
                    });
                  });
                } else {
                  const roomDefinitions = buildCategoryRoomDefinitions(
                    String(category.categoryId),
                    configuredRooms,
                    roomNumbersByCategory
                  );
                  const roomIndexById = new Map();
                  const roomIndexByNumber = new Map();

                  roomDefinitions.forEach((roomDef, roomIndex) => {
                    if (roomDef?.roomId) roomIndexById.set(roomDef.roomId, roomIndex);
                    if (roomDef?.roomNumber) roomIndexByNumber.set(String(roomDef.roomNumber).toLowerCase(), roomIndex);
                  });

                  const reservationsByRoom = roomDefinitions.map(() => []);
                  const unassignedReservations = [];

                  for (const reservation of category.visibleReservations) {
                    const isAssignableReservation = reservation?.__type !== "room_block";
                    const roomReference = normalizeRoomReference(reservation);
                    let targetIndex = roomReference.roomId ? roomIndexById.get(roomReference.roomId) : undefined;
                    if (targetIndex == null && roomReference.roomNumberKey) {
                      targetIndex = roomIndexByNumber.get(roomReference.roomNumberKey);
                    }

                    if (targetIndex == null) {
                      if (isAssignableReservation) unassignedReservations.push(reservation);
                      continue;
                    }

                    reservationsByRoom[targetIndex].push(reservation);
                  }

                  roomDefinitions.forEach((roomDef, roomIndex) => {
                    const roomLanes = buildFixedLanesForCategory(reservationsByRoom[roomIndex], 1);
                    const primaryLaneItems = Array.isArray(roomLanes[0]) ? roomLanes[0] : [];
                    categoryRows.push({
                      rowKey: `${category.categoryId}-room-${roomIndex}-lane-0`,
                      laneItems: primaryLaneItems,
                      roomId: roomDef.roomId || "",
                      categoryId: String(category.categoryId),
                      categoryName: category.categoryName,
                      roomLabel: roomDef.fallbackNumber != null ? t("Room {number}", { number: roomDef.fallbackNumber }) : roomDef.roomNumber,
                      isOverflow: false,
                      isUnassignedSummary: false,
                    });

                    // Never render overlap lanes as extra rows.
                    // If a second lane exists for the same room number, keep those reservations in unassigned summary.
                    for (let laneIdx = 1; laneIdx < roomLanes.length; laneIdx += 1) {
                      const conflictLaneItems = Array.isArray(roomLanes[laneIdx]) ? roomLanes[laneIdx] : [];
                      conflictLaneItems.forEach((laneItem) => {
                        const reservation = laneItem?.reservation;
                        if (!reservation || reservation?.__type === "room_block") return;
                        unassignedReservations.push(reservation);
                      });
                    }
                  });

                  const unassignedByDate = {};
                  const unassignedLabelsByDate = {};
                  const unassignedReservationsByDate = {};

                  visibleDates.forEach((dateIso) => {
                    const labels = [];
                    const reservationsOnDate = [];
                    for (const reservation of unassignedReservations) {
                      if (!reservationOccupiesDate(reservation, dateIso)) continue;
                      labels.push(getReservationLabelForGrid(reservation));
                      reservationsOnDate.push(reservation);
                    }
                    if (labels.length > 0) {
                      unassignedByDate[dateIso] = labels.length;
                      unassignedLabelsByDate[dateIso] = labels;
                      unassignedReservationsByDate[dateIso] = reservationsOnDate;
                    }
                  });

                  categoryRows.push({
                    rowKey: `${category.categoryId}-unassigned-summary`,
                    laneItems: [],
                    roomId: "",
                    categoryId: String(category.categoryId),
                    categoryName: category.categoryName,
                    roomLabel: t("Room not assigned"),
                    isOverflow: false,
                    isUnassignedSummary: true,
                    unassignedTotal: unassignedReservations.length,
                    unassignedByDate,
                    unassignedLabelsByDate,
                    unassignedReservationsByDate,
                  });
                }

                const categoryRowSpan = Math.max(1, categoryRows.length);

                return categoryRows.map((row, rowIndex) => {
                  const baseRowClassName = categoryIdx > 0 && rowIndex === 0 ? "res-grid-category-separator" : "";
                  const roomCellClassName = `rm-sticky-2 res-grid-room-cell${row.isOverflow ? " is-overflow" : ""}${
                    row.isUnassignedSummary ? " is-unassigned-summary" : ""
                  }`;
                  const rowDropKey = `${row.categoryId}:${row.roomId}`;
                  const unassignedDropKey = `${row.categoryId}:unassigned`;
                  const rowCanAcceptDrop = Boolean(canAssignRooms && usingRoomNumbers && !row.isUnassignedSummary && row.roomId);
                  const rowCanAcceptUnassignDrop = Boolean(canAssignRooms && usingRoomNumbers && row.isUnassignedSummary);
                  const rowClassName = [
                    baseRowClassName,
                    rowCanAcceptDrop && draggedReservationId ? "res-grid-drop-target" : "",
                    rowCanAcceptUnassignDrop && draggedReservationId ? "res-grid-unassign-drop-target" : "",
                  ].filter(Boolean);
                  if (rowCanAcceptDrop && dragOverTargetKey === rowDropKey) rowClassName.push("is-drag-over");
                  if (rowCanAcceptUnassignDrop && dragOverTargetKey === unassignedDropKey) rowClassName.push("is-drag-over");

                  return (
                    <tr
                      key={row.rowKey}
                      className={rowClassName.join(" ") || undefined}
                      onDragOver={(event) => {
                        if (!rowCanAcceptDrop) return;
                        const payload = readDragPayload(event);
                        if (!canDropPayloadOnRoom(payload, row.categoryId, row.roomId)) return;
                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                        if (dragOverTargetKey !== rowDropKey) setDragOverTargetKey(rowDropKey);
                      }}
                      onDragEnter={(event) => {
                        if (!rowCanAcceptDrop) return;
                        const payload = readDragPayload(event);
                        if (!canDropPayloadOnRoom(payload, row.categoryId, row.roomId)) return;
                        event.preventDefault();
                        if (dragOverTargetKey !== rowDropKey) setDragOverTargetKey(rowDropKey);
                      }}
                      onDragLeave={(event) => {
                        if (!rowCanAcceptDrop) return;
                        if (event.currentTarget.contains(event.relatedTarget)) return;
                        if (dragOverTargetKey === rowDropKey) setDragOverTargetKey("");
                      }}
                      onDrop={(event) => {
                        if (!rowCanAcceptDrop) return;
                        setDragOverTargetKey("");
                        void handleDropOnRoomRow(event, row.categoryId, row.roomId);
                      }}
                    >
                      {rowIndex === 0 ? (
                        <td rowSpan={categoryRowSpan} className="rm-sticky-1 res-grid-cat-cell">
                          <div className="res-grid-cat-name">{category.categoryName || t("Category {id}", { id: category.categoryId })}</div>
                        </td>
                      ) : null}

                      <td
                        className={roomCellClassName}
                        onDragOver={(event) => {
                          if (!rowCanAcceptUnassignDrop) return;
                          const payload = readDragPayload(event);
                          if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          if (dragOverTargetKey !== unassignedDropKey) setDragOverTargetKey(unassignedDropKey);
                        }}
                        onDragEnter={(event) => {
                          if (!rowCanAcceptUnassignDrop) return;
                          const payload = readDragPayload(event);
                          if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                          event.preventDefault();
                          if (dragOverTargetKey !== unassignedDropKey) setDragOverTargetKey(unassignedDropKey);
                        }}
                        onDragLeave={(event) => {
                          if (!rowCanAcceptUnassignDrop) return;
                          if (event.currentTarget.contains(event.relatedTarget)) return;
                          if (dragOverTargetKey === unassignedDropKey) setDragOverTargetKey("");
                        }}
                        onDrop={(event) => {
                          if (!rowCanAcceptUnassignDrop) return;
                          const payload = readDragPayload(event);
                          if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                          event.preventDefault();
                          setDragOverTargetKey("");
                          void handleDropOnUnassignedCell({
                            payload,
                            dateIso: "",
                            targetCategoryId: row.categoryId,
                          });
                        }}
                      >
                        {row.isUnassignedSummary ? (
                          <>
                            {row.roomLabel}
                            <span className="res-grid-unassigned-count">{row.unassignedTotal || 0} {t("in selected range")}</span>
                          </>
                        ) : (
                          row.roomLabel
                        )}
                      </td>

                      {row.isUnassignedSummary
                        ? visibleDates.map((dateIso, dateIdx) => {
                            const dayIsOverbooked = overbookedDates.has(dateIso);
                            const overbookTooltip = dayIsOverbooked ? overflowTooltipByDate[dateIso] || "" : "";
                            const unassignedCount = Number(row.unassignedByDate?.[dateIso] ?? 0);
                            const unassignedLabels = row.unassignedLabelsByDate?.[dateIso] ?? [];
                            const unassignedReservations = row.unassignedReservationsByDate?.[dateIso] ?? [];
                            const unassignedTooltip =
                              unassignedCount > 0 ? buildUnassignedTooltip(toDayLabel(dateIso, locale), unassignedLabels, t) : "";
                            const tooltip = [overbookTooltip, unassignedTooltip].filter(Boolean).join("\n");

                            return (
                              <td
                                key={`${row.rowKey}-unassigned-${dateIdx}`}
                                className={`res-grid-day-cell is-unassigned-summary${
                                  dateIso === currentDateIso ? " is-today" : ""
                                }${
                                  dayIsOverbooked ? " is-overbook" : ""
                                }`}
                                title={tooltip || undefined}
                                onDragOver={(event) => {
                                  if (!usingRoomNumbers) return;
                                  const payload = readDragPayload(event);
                                  if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                                  event.preventDefault();
                                  event.dataTransfer.dropEffect = "move";
                                  if (dragOverTargetKey !== unassignedDropKey) setDragOverTargetKey(unassignedDropKey);
                                }}
                                onDragEnter={(event) => {
                                  if (!usingRoomNumbers) return;
                                  const payload = readDragPayload(event);
                                  if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                                  event.preventDefault();
                                  if (dragOverTargetKey !== unassignedDropKey) setDragOverTargetKey(unassignedDropKey);
                                }}
                                onDragLeave={(event) => {
                                  if (!usingRoomNumbers) return;
                                  if (event.currentTarget.contains(event.relatedTarget)) return;
                                  if (dragOverTargetKey === unassignedDropKey) setDragOverTargetKey("");
                                }}
                                onDrop={(event) => {
                                  if (!usingRoomNumbers) return;
                                  const payload = readDragPayload(event);
                                  if (!canDropPayloadOnUnassigned(payload, row.categoryId)) return;
                                  event.preventDefault();
                                  setDragOverTargetKey("");
                                  void handleDropOnUnassignedCell({
                                    payload,
                                    dateIso,
                                    targetCategoryId: row.categoryId,
                                  });
                                }}
                              >
                                {unassignedCount > 0 ? (
                                  <button
                                    type="button"
                                    className="res-grid-unassigned-value res-grid-unassigned-value-btn"
                                    onClick={() =>
                                      openUnassignedPanel(
                                        row.categoryId,
                                        row.categoryName,
                                        dateIso,
                                        unassignedReservations
                                      )
                                    }
                                  >
                                    {unassignedCount}
                                  </button>
                                ) : (
                                  <span className="res-grid-unassigned-value is-empty">—</span>
                                )}
                              </td>
                            );
                          })
                        : buildCellsForLane(
                            {
                              categoryId: row.categoryId,
                              roomId: row.roomId,
                            },
                            row.rowKey,
                            row.laneItems,
                            overbookedDates,
                            overflowTooltipByDate,
                            handleDropOnRoomCell
                          )}
                    </tr>
                  );
                });
              })}
            </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {usingRoomNumbers && unassignedPanel ? (
        <div className="res-grid-floatPanel" role="dialog" aria-modal="false" aria-label={t("Unassigned reservations")}>
          <div className="res-grid-floatHead">
            <div>
              <h4 className="res-grid-floatTitle">{t("Room not assigned")}</h4>
              <div className="res-grid-floatMeta">
                {categories.find((category) => String(category.categoryId) === String(unassignedPanel.categoryId))?.categoryName || t("Category {id}", { id: unassignedPanel.categoryId })} · {toDayLabel(unassignedPanel.dateIso, locale)}
              </div>
            </div>
            <button type="button" className="res-grid-floatClose" onClick={closeUnassignedPanel} aria-label={t("Close panel")}>
              <FiX aria-hidden="true" />
            </button>
          </div>

          <div className="res-grid-floatBody">
            <div className="res-grid-floatHint">
              {readOnly
                ? t("Read only. Open a reservation to view its details.")
                : t("Drag each reservation to a room row, or assign directly from the selector.")}
            </div>

            {panelError ? <div className="res-grid-floatError">{translateError(panelError)}</div> : null}

            {panelLoading ? <div className="res-grid-floatEmpty">{t("Loading available room numbers...")}</div> : null}

            {!panelLoading && unassignedPanel.items.length === 0 ? (
              <div className="res-grid-floatEmpty">{t("No unassigned reservations left for this day.")}</div>
            ) : null}

            {!panelLoading
                ? unassignedPanel.items.map((item) => {
                  const reservation = item.reservation;
                  const reservationId = item.reservationId;
                  const availableRooms = liveRoomsByReservation[reservationId] || [];
                  const selectedRoomId = proposedRoomByReservation[reservationId] || "";
                  const suggestedRoomIdRaw =
                    reservation?.suggested_room_id ??
                    reservation?.suggested_room?.id ??
                    null;
                  const suggestedRoomId = suggestedRoomIdRaw != null ? String(suggestedRoomIdRaw) : "";
                  const suggestedRoomNumber =
                    reservation?.suggested_room?.room_number ||
                    reservation?.suggested_room_number ||
                    (suggestedRoomId ? roomLabelById?.[suggestedRoomId] || "" : "");
                  const hasSuggestedRoom = String(suggestedRoomNumber || "").trim().length > 0;
                  const isDragging = draggedReservationId === reservationId;
                  const isInHousePanelItem = isInHouseReservation(reservation);

                return (
                  <div
                    key={reservationId}
                    className={`res-grid-floatItem${isDragging ? " is-dragging" : ""}`}
                  >
                    <div className="res-grid-floatItemMain">
                      <div className="res-grid-floatBadgeRow">
                        <button
                          type="button"
                          className={`res-grid-floatBadge${isDragging ? " is-dragging" : ""}${isInHousePanelItem ? " is-in-house" : ""}`}
                          draggable={canAssignRooms}
                          disabled={readOnly && typeof onOpenReservation !== "function"}
                          onClick={() => {
                            if (readOnly && typeof onOpenReservation === "function") onOpenReservation(reservation);
                          }}
                          onDragStart={(event) => {
                            if (!canAssignRoomsRef.current) return;
                            const payload = {
                              kind: "unassigned_panel",
                              reservationId,
                              categoryId: String(unassignedPanel?.categoryId || ""),
                            };
                            clearOperationError();
                            setDragPayload(payload, event);
                            setDraggedReservationId(reservationId);
                          }}
                          onDragEnd={() => {
                            setDraggedReservationId("");
                            setDragOverTargetKey("");
                            dragPayloadRef.current = null;
                          }}
                          aria-label={readOnly
                            ? t("Open reservation {name}", { name: getReservationLabelForGrid(reservation) })
                            : t("Drag {name} to a room row", { name: getReservationLabelForGrid(reservation) })}
                          style={readOnly ? { cursor: "pointer" } : undefined}
                        >
                          <span className="res-grid-floatBadgeLabel">{getReservationLabelForGrid(reservation)}</span>
                          <span className="res-grid-floatDragHint">{readOnly ? t("Open") : t("Drag")}</span>
                        </button>
                        <div className="res-grid-floatBadgeInfo">
                          <div className="res-grid-floatItemMeta">
                            {reservation?.check_in_date} {t("to")} {reservation?.check_out_date}
                          </div>
                          {hasSuggestedRoom ? (
                            <div className="res-grid-floatItemMeta">{t("Room suggested:")} {suggestedRoomNumber}</div>
                          ) : null}
                          <span className="res-grid-floatBadgeHelper">{readOnly ? t("Read only") : t("Drag this capsule into a room")}</span>
                        </div>
                      </div>
                    </div>

                    <div className="res-grid-floatAssignRow">
                        <select
                          className="res-grid-floatSelect"
                          aria-label={t("Room for {name}", { name: getReservationLabelForGrid(reservation) })}
                          value={selectedRoomId}
                          disabled={readOnly}
                          title={readOnly ? t("Room assignment is disabled in the demo") : undefined}
                          onChange={(event) => {
                            if (readOnly) return;
                            setPriorityRoomReservationId(reservationId);
                            setSelectedRoomByReservation(getDistinctRoomProposals({
                              items: unassignedPanel.items,
                              availableRoomsByReservation: liveRoomsByReservation,
                              preferredRoomByReservation: {
                                ...proposedRoomByReservation,
                                [reservationId]: event.target.value,
                              },
                              priorityReservationId: reservationId,
                            }));
                          }}
                        >
                          {!selectedRoomId && availableRooms.length > 0 ? (
                            <option value="" disabled>{t("Select a room")}</option>
                          ) : null}
                          {availableRooms.length === 0 ? (
                            <option value="">{readOnly ? t("Read only") : t("No available room")}</option>
                          ) : (
                            availableRooms.map((room) => (
                              <option key={room.id ?? room.room_id} value={String(room.id ?? room.room_id)}>
                                {room?.room_number || t("Room {number}", { number: room?.id ?? room?.room_id })}
                              </option>
                            ))
                          )}
                        </select>

                        <button
                          type="button"
                          className="res-grid-floatAssignBtn"
                          disabled={!canAssignRooms || !selectedRoomId}
                          title={readOnly ? t("Room assignment is disabled in the demo") : undefined}
                          onClick={() => {
                            void assignPanelReservation(item, selectedRoomId);
                          }}
                        >
                          {t("Assign")}
                        </button>
                    </div>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
